/**
 * Meal Plan A4 PDF export — matches the Figma reference layout:
 * clinic header, client details, two day columns per page, meal slots with times,
 * recipe servings, comma-separated ingredient lists, and optional instructions.
 */

import { jsPDF } from 'jspdf';
import { differenceInYears, format } from 'date-fns';
import { GENDER_OPTIONS, SEX_AT_BIRTH_OPTIONS } from '@/clients/onboarding/components/constants';
import { getValidAllergies, getValidFoodIntolerances, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { getNutritionistProfile, resolveNutritionistPdfBranding } from '@/lib/api/auth';
import {
  getCustomRecipeDetails,
  getCustomizedRecipeDetails,
  getGenericMealPlanExportForPdf,
  getMealPlanExportForPdf,
  getMealPlanTemplate,
  getRecipeDetails,
} from '@/lib/api/mealPlans';
import type {
  MealPlanTemplateDetails,
  MealPlanTemplateItem,
  MealPlanTemplateMeal,
} from '@/lib/api/mealPlans';
import type {
  MealPlanExportDay,
  MealPlanExportForPdfData,
  MealPlanExportItem,
  MealPlanExportMeal,
  MealPlanExportRecipeDetails,
} from '@/types/meal-plan';
import {
  flattenExportItemRecord,
  formatIngredientAmountAndUnit,
  normalizeGenericMealPlanExportForPdf,
  unwrapMealPlanExportData,
} from '@/utils/meal-plan-export';
import { formatWeight, formatHeight } from '@/utils/unit-conversion';
import { formatDelimitedDisplayLabel, formatDisplayLabel, formatDisplayLabelList } from '@/utils/display-labels';
import logo2Svg from '@/assets/images/landing/logo_2.svg';

// ============================================================================
// Layout (reference A4 frame)
// ============================================================================
const PAGE_WIDTH = 792;
const PAGE_HEIGHT = 1120;
/** The reference PDF is exported from a 792x1120 Figma frame, so 1 design px = 1 PDF unit. */
const px = (value: number): number => value;

const MARGIN = px(32);
const COLUMN_GAP = px(10);
const COLUMN_WIDTH = (PAGE_WIDTH - 2 * MARGIN - COLUMN_GAP) / 2;
const LEFT_COL_X = MARGIN;
const RIGHT_COL_X = MARGIN + COLUMN_WIDTH + COLUMN_GAP;
const FOOTER_Y = PAGE_HEIGHT - px(32);
const FOOTER_RULE_Y = PAGE_HEIGHT - px(16);
/** Keep meal content above the footer text and page number. */
const PAGE_BOTTOM_SAFE_GAP = px(24);
const CONTENT_BOTTOM = FOOTER_Y - PAGE_BOTTOM_SAFE_GAP;

const COLORS = {
  primary: '#3ba1e6',
  primaryDark: '#1d8eb8',
  textDark: '#1d2b2d',
  textBody: '#344054',
  textMuted: '#667085',
  textLight: '#98a2b3',
  border: '#dbe6eb',
  logoPlaceholder: '#e5e7eb',
  white: '#ffffff',
  /** Day title bar fill (reference A4 layout). */
  dayBg: '#eaf8f3',
  /** Gray outline around the full day column. */
  dayContainerBorder: '#dfe8e8',
  mealSlotHeaderBg: '#eef5f9',
  mealSlotHeaderText: '#0f3440',
  ingredientDivider: '#e4e7ec',
};

const BANNER_HEIGHT = px(48);
const BANNER_LOGO_SIZE = px(32);
const BANNER_LOGO_TOP = px(8);
const BANNER_TEXT_Y = px(27);
const BANNER_TEXT_GAP = px(8);
/** Client name: 16px / 27px line-height, #111827 (Helvetica bold — no custom font load). */
const CLIENT_NAME_COLOR = '#111827';
const PX_TO_PT = 1;
const CLIENT_NAME_FONT_SIZE_PT = 16 * PX_TO_PT;
const CLIENT_NAME_LINE_HEIGHT_MM = px(27);
const CLIENT_META_FONT_SIZE = 12;
const CLIENT_DATE_FONT_SIZE = 12;
const DIET_PLANS_FONT_SIZE = 16;
/** Vertical gaps in the client header block. */
const CLIENT_BLOCK_TOP_GAP = px(28);
const CLIENT_NAME_TO_META_GAP = 6;
const CLIENT_META_ROW_GAP = px(28);
const CLIENT_META_SEGMENT_GAP = px(32);
const CLIENT_BLOCK_BOTTOM_GAP = 0;
const CLIENT_DIVIDER_GAP = px(35);
const CLIENT_DETAILS_DIVIDER_PULLUP = px(5);
const CLIENT_FALLBACK_DIVIDER_Y = BANNER_HEIGHT + px(105);
const PT_TO_MM = 1;

function fontAscenderMm(fontSizePt: number): number {
  return fontSizePt * PT_TO_MM * 0.72;
}

function fontDescenderMm(fontSizePt: number): number {
  return fontSizePt * PT_TO_MM * 0.28;
}

const INGREDIENT_FONT_SIZE_PT = 12;
const INSTRUCTION_FONT_SIZE_PT = 12;
const INSTRUCTION_LABEL_FONT_SIZE_PT = 12;
const RECIPE_NAME_FONT_SIZE_PT = 14;
const RECIPE_NAME_LINE_HEIGHT = px(20);
const RECIPE_INTRO_ROW_MIN_HEIGHT = px(40);
const SERVING_FONT_SIZE_PT = 12;
const SERVING_LINE_HEIGHT = px(18);
/** From servings/name baseline to intro end when the ingredient divider follows (tight). */
const RECIPE_INTRO_TO_DIVIDER_GAP = px(10);
/** Baseline-to-baseline leading inside wrapped ingredient names. */
const INGREDIENT_WRAP_LINE_HEIGHT = px(14);
/** Space after recipe intro/serving before the ingredient table divider. */
const INGREDIENT_TABLE_TOP_GAP = px(2);
/** Minimum divider-to-divider height for each ingredient row. */
const INGREDIENT_ROW_MIN_HEIGHT = px(22);
const INGREDIENT_BLOCK_TOP_GAP = px(8);
const INGREDIENT_DIVIDER_ABOVE_MM = px(8);
const INGREDIENT_DIVIDER_BELOW_MM = px(6);
/** Space below the last ingredient row (before instructions). */
const INGREDIENT_BLOCK_BOTTOM_GAP = px(6);
/** Tail space after the last ingredient divider. */
const INGREDIENT_LAST_ROW_TAIL = 0;
const INSTRUCTION_NUMBER_WIDTH = px(18);
const INSTRUCTION_POINT_GAP = px(7);
const INSTRUCTION_SECTION_TOP_GAP = px(14);
const NUTRITION_BLOCK_TOP_GAP = px(12);
const NUTRITION_HEADING_HEIGHT = px(20);
const NUTRITION_CARD_TOP_GAP = px(9);
const NUTRITION_CARD_HEIGHT = px(178);
const NUTRITION_BLOCK_BOTTOM_GAP = px(18);
const NUTRITION_BLOCK_HEIGHT =
  NUTRITION_BLOCK_TOP_GAP +
  NUTRITION_HEADING_HEIGHT +
  NUTRITION_CARD_TOP_GAP +
  NUTRITION_CARD_HEIGHT +
  NUTRITION_BLOCK_BOTTOM_GAP;
/** Space below "Diet Plans" before Day 1 / Day 2 columns. */
const DIET_PLANS_BOTTOM_GAP = px(29);
/** Practitioner note typography (plan / day / meal / item). */
const NOTE_FONT_SIZE_PT = 11;
const NOTE_LINE_HEIGHT = px(15);
const NOTE_BLOCK_TOP_GAP = px(6);
const NOTE_BLOCK_BOTTOM_GAP = px(6);
/** Align notes with recipe/day content (col.x already includes day padding). */
const NOTE_LEFT_INSET = px(0);
const NOTE_TEXT_COLOR = '#475467';
/** Extra space after day title before a day note. */
const DAY_NOTE_TOP_GAP = px(12);
/** Extra space after a day note before the first meal slot. */
const DAY_NOTE_BOTTOM_GAP = px(16);
/** Space under meal-slot bar before a meal note. */
const MEAL_NOTE_TOP_GAP = px(10);
/** Space after a meal note before recipes/ingredients. */
const MEAL_NOTE_BOTTOM_GAP = px(14);
/** Horizontal inset for meal content inside the day border. */
const DAY_PADDING = px(12);
const DAY_HEADER_HEIGHT = px(44);
/** Baseline offset for "Day N" text inside the title bar. */
const DAY_HEADER_TEXT_Y = px(26);
/** Gap between day title bar and first meal. */
const DAY_CONTENT_TOP_GAP = px(24);
/** Outer border wrapping header + all meals for the day. */
const DAY_CONTAINER_RADIUS = px(10);
const DAY_INNER_WIDTH = COLUMN_WIDTH - 2 * DAY_PADDING;
/** Vertical space between meal sections (same day). */
const MEAL_GAP = px(10);
/** Small tail padding after the last item in a meal slot. */
const MEAL_SLOT_BOTTOM_GAP = px(6);
/** Divider + padding between recipes within a meal. */
const RECIPE_SEPARATOR_BEFORE_GAP = px(12);
const RECIPE_SEPARATOR_AFTER_GAP = px(18);
const RECIPE_SEPARATOR_HEIGHT = RECIPE_SEPARATOR_BEFORE_GAP + RECIPE_SEPARATOR_AFTER_GAP;
const CHOICE_HEADER_HEIGHT = px(40);
const CHOICE_HEADER_BG = '#f8fafc';
const CHOICE_HEADER_BORDER = '#e2e8f0';
const CHOICE_CONTAINER_RADIUS = px(6);
const CHOICE_BADGE_SIZE = px(20);
const CHOICE_BADGE_TEXT_GAP = px(10);
/** Align choice body text with the header label beside the numbered badge. */
const CHOICE_BODY_TEXT_INDENT = CHOICE_BADGE_SIZE + CHOICE_BADGE_TEXT_GAP;
const CHOICE_SECTION_GAP_HEIGHT = px(22);
const AND_CHOICE_DIVIDER_HEIGHT = px(30);
const MEAL_SLOT_HEADER_HEIGHT = px(36);
const MEAL_SLOT_HEADER_RADIUS = px(6);
const MEAL_SLOT_HEADER_PADDING_X = px(8);
const MEAL_SLOT_HEADER_TEXT_Y = px(24);
const MEAL_SLOT_TIME_DIVIDER_X = px(88);
const MEAL_SLOT_DIVIDER_TOP = px(10);
const MEAL_SLOT_DIVIDER_BOTTOM = px(26);
const MEAL_SLOT_LABEL_GAP = px(16);
/** Baseline offset from bottom of meal time bar to first recipe name (includes visual gap). */
const MEAL_RECIPE_TOP_OFFSET = px(16);
const CHOICE_CONTENT_TOP_PADDING = MEAL_RECIPE_TOP_OFFSET;
const CHOICE_CONTENT_BOTTOM_PADDING = DAY_PADDING;
const DAY_BORDER_LINE_WIDTH = 1;

const MEAL_TIME_FALLBACK: Record<string, string> = {
  'pre-breakfast': '06:30 AM',
  breakfast: '07:30 AM',
  'mid-morning-snack': '10:30 AM',
  snack: '07:30 PM',
  lunch: '01:30 PM',
  'afternoon-snack': '05:00 PM',
  dinner: '08:30 PM',
};

// ============================================================================
// Types
// ============================================================================
export interface ClientPDFInfo {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  /** When export API provides age without date of birth. */
  age?: number;
  gender?: string;
  sexAtBirth?: string;
  occupation?: string;
  weightKg?: number;
  heightCm?: number;
  bmi?: number;
  preferredMeasurementSystem?: 'metric' | 'imperial';
  medicalConditions?: string[] | string;
  allergies?: string[];
  foodIntolerances?: string[];
  clientGoal?: { allergies?: string[] };
}

/** Raw client shapes accepted before PDF normalization. */
export type ClientPdfSource = ClientPDFInfo & {
  medicalConditionDetails?: Array<{
    conditionId?: string;
    conditionName?: string;
    displayName?: string;
  }>;
  healthMetrics?: { bmi?: number };
  dietaryPreferences?: string[];
};

export interface PDFIngredient {
  quantity?: number;
  amount?: number;
  unit?: string;
  name?: string;
  originalString?: string;
}

export interface PDFFoodRow {
  name: string;
  quantity: string;
}

export type RecipePdfNutritionSummary = {
  calories?: number | null;
  carbs?: number | null;
  protein?: number | null;
  fat?: number | null;
};

export interface PDFRecipe {
  recipeName: string;
  servingLabel?: string;
  /** Practitioner item notes (`notes` / `customNotes`). */
  notes?: string;
  foodRows?: PDFFoodRow[];
  ingredients?: PDFIngredient[];
  ingredientLines?: string[];
  instructions?: string[];
  nutritionSummary?: RecipePdfNutritionSummary;
  suppressNutritionSummary?: boolean;
  itemType?: string;
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
}

export interface PDFMeal {
  mealType: string;
  mealLabel?: string;
  mealTime?: string;
  /** Practitioner meal-slot notes. */
  notes?: string;
  recipes: PDFRecipe[];
}

export interface PDFDay {
  day: number;
  date?: string;
  dayName?: string;
  /** Practitioner day notes (separate from dayName). */
  notes?: string;
  meals: PDFMeal[];
}

export interface MealPlanPDFData {
  days: PDFDay[];
  /** Practitioner plan notes (`description` preferred, else `planNotes`). */
  planNotes?: string;
}

export interface MealPlanPdfExportResult {
  pdfData: MealPlanPDFData;
  clientInfo: ClientPDFInfo | null;
  clientDisplayName?: string;
  branding: { clinicName?: string; clinicLogoUrl?: string };
  planTitle?: string;
  planDate?: string;
  planNotes?: string;
}

export interface GenerateMealPlanPDFOptions {
  includeInstructions?: boolean;
  includeClientDetails?: boolean;
  /** When no clinic logo/name, fall back to the CalorieScience logo. */
  includeLogo?: boolean;
  clinicName?: string;
  clinicLogoUrl?: string;
  planTitle?: string;
  planDate?: string | Date;
  documentLabel?: string;
  sectionTitle?: string;
  /** Practitioner plan-level note shown under the Diet Plans heading. */
  planNotes?: string;
  singleDayFullWidth?: boolean;
  /** Generic meal plans: omit the "Day N" title bar only; keep the meal-plan container border. */
  hideDayStructure?: boolean;
}

type DayLayoutOptions = {
  hideDayStructure?: boolean;
  showGenericOptionGroups?: boolean;
};

type PDFOptionGroup = {
  optionGroupId: string | null;
  label: string;
  recipes: PDFRecipe[];
};

type CachedLogoImage = {
  dataUrl: string;
  width: number;
  height: number;
};

type PdfBannerBranding =
  | { mode: 'image'; image: CachedLogoImage; label?: string }
  | { mode: 'text'; label: string }
  | { mode: 'placeholder' };

type PdfHeaderOptions = {
  branding: PdfBannerBranding;
  clientDetails: ClientPDFInfo | null;
  planTitle?: string;
  planDateLabel: string;
  documentLabel?: string;
  sectionTitle?: string;
  planNotes?: string;
};

let cachedLogoImage: CachedLogoImage | null = null;
let loadingLogoImage: Promise<CachedLogoImage | null> | null = null;
const cachedClinicLogos = new Map<string, CachedLogoImage>();
const loadingClinicLogos = new Map<string, Promise<CachedLogoImage | null>>();
let loadingProfilePdfBranding: Promise<{ clinicName?: string; clinicLogoUrl?: string }> | null = null;

// ============================================================================
// Color / text helpers
// ============================================================================
function setTextColor(doc: jsPDF, hexColor: string): void {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  doc.setTextColor(r, g, b);
}

function setFillColor(doc: jsPDF, hexColor: string): void {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  doc.setFillColor(r, g, b);
}

function setDrawColor(doc: jsPDF, hexColor: string): void {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  doc.setDrawColor(r, g, b);
}

function wrapText(doc: jsPDF, text: string, maxWidth: number): string[] {
  const split = doc.splitTextToSize(text, maxWidth);
  return Array.isArray(split) ? split : [text];
}

function normalizePractitionerNote(value: unknown): string | undefined {
  const text = String(value ?? '').trim();
  return text.length > 0 ? text : undefined;
}

/** Prefer finalized `description`, then draft `planNotes`. */
function resolvePlanNotesFromExport(data: MealPlanExportForPdfData): string | undefined {
  return (
    normalizePractitionerNote(data.description) ??
    normalizePractitionerNote(data.planNotes) ??
    normalizePractitionerNote((data as Record<string, unknown>).plan_notes)
  );
}

function resolveItemNotesFromRecord(record: Record<string, unknown>): string | undefined {
  return (
    normalizePractitionerNote(record.notes) ??
    normalizePractitionerNote(record.customNotes) ??
    normalizePractitionerNote(record.custom_notes)
  );
}

function measureNoteBlockHeight(doc: jsPDF, note: string, maxWidth: number): number {
  const textWidth = Math.max(0, maxWidth - NOTE_LEFT_INSET);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(NOTE_FONT_SIZE_PT);
  const lines = wrapText(doc, note, textWidth);
  return NOTE_BLOCK_TOP_GAP + lines.length * NOTE_LINE_HEIGHT + NOTE_BLOCK_BOTTOM_GAP;
}

function renderNoteBlock(doc: jsPDF, col: ColumnState, maxWidth: number, note: string): void {
  const textWidth = Math.max(0, maxWidth - NOTE_LEFT_INSET);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(NOTE_FONT_SIZE_PT);
  const lines = wrapText(doc, note, textWidth);
  if (lines.length === 0) return;

  col.y += NOTE_BLOCK_TOP_GAP;
  const textX = col.x + NOTE_LEFT_INSET;
  setTextColor(doc, NOTE_TEXT_COLOR);
  for (const line of lines) {
    doc.text(line, textX, col.y);
    col.y += NOTE_LINE_HEIGHT;
  }
  col.y += NOTE_BLOCK_BOTTOM_GAP;
}

function createNoteLayoutBlock(
  doc: jsPDF,
  note: string | undefined,
  maxWidth: number,
  wrapInDayBorder = true,
): LayoutBlock | null {
  const trimmed = normalizePractitionerNote(note);
  if (!trimmed) return null;
  const height = measureNoteBlockHeight(doc, trimmed, maxWidth);
  return {
    height,
    wrapInDayBorder,
    render(pdfDoc, col) {
      renderNoteBlock(pdfDoc, col, maxWidth, trimmed);
    },
  };
}

function convertTo12HourFormat(time24: string): string {
  if (!time24 || time24 === 'N/A' || time24 === '--') return '';
  const trimmed = time24.trim();
  const twelveHourMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    return `${twelveHourMatch[1].padStart(2, '0')}:${twelveHourMatch[2]} ${twelveHourMatch[3].toUpperCase()}`;
  }
  if (/am|pm/i.test(trimmed)) return trimmed;

  const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!timeMatch) return trimmed;

  let hours = parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${String(hours).padStart(2, '0')}:${minutes} ${period}`;
}

function toMealLabel(value: string): string {
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeMealTypeKey(mealType: string): string {
  return mealType.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
}

function resolveMealTime(meal: PDFMeal): string {
  const explicit = convertTo12HourFormat(meal.mealTime?.trim() ?? '');
  if (explicit) return explicit;
  return MEAL_TIME_FALLBACK[normalizeMealTypeKey(meal.mealType)] ?? '';
}

function formatQty(value: number): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const intPart = Math.floor(num);
  const decimalPart = num - intPart;
  if (Math.abs(decimalPart - 0.25) < 0.01) return intPart > 0 ? `${intPart}¼` : '¼';
  if (Math.abs(decimalPart - 0.33) < 0.02) return intPart > 0 ? `${intPart}⅓` : '⅓';
  if (Math.abs(decimalPart - 0.5) < 0.01) return intPart > 0 ? `${intPart}½` : '½';
  if (Math.abs(decimalPart - 0.67) < 0.02) return intPart > 0 ? `${intPart}⅔` : '⅔';
  if (Math.abs(decimalPart - 0.75) < 0.01) return intPart > 0 ? `${intPart}¾` : '¾';
  if (decimalPart < 0.01) return String(intPart);
  return num.toFixed(1).replace(/\.0$/, '');
}

function cleanIngredientName(rawName: string): string {
  return rawName
    .trim()
    .replace(
      /\s*[-–—]\s*(?:\d+(?:\.\d+)?|[¼½¾⅓⅔])(?:\s*(?:to|-|–|—)\s*(?:\d+(?:\.\d+)?|[¼½¾⅓⅔]))?\s*(?:g|gm|grms|ml|kg|oz|lb|cup|cups|tbsp|tsp|piece|pieces|bowl|bowls|serving|servings)?\s*$/i,
      '',
    )
    .trim();
}

function normalizeFoodRow(row: PDFFoodRow): PDFFoodRow {
  return {
    ...row,
    name: cleanIngredientName(row.name || ''),
  };
}

function parseIngredientString(line: string): PDFFoodRow {
  const trimmed = line.trim();
  if (!trimmed) return { name: '', quantity: '' };
  const match = trimmed.match(/^([\d./¼½¾⅓⅔\s]+(?:g|gm|grms|ml|kg|oz|lb|cup|cups|tbsp|tsp|piece|pieces|bowl|bowls|serving|servings)?)\s+(.+)$/i);
  if (match) return normalizeFoodRow({ quantity: match[1].trim(), name: match[2].trim() });
  const reverse = trimmed.match(/^(.+?)\s+([\d./¼½¾⅓⅔]+(?:\s*(?:g|gm|grms|ml|kg|oz|lb|cup|cups|tbsp|tsp|piece|pieces|bowl|bowls|serving|servings))?)$/i);
  if (reverse) return normalizeFoodRow({ name: reverse[1].trim(), quantity: reverse[2].trim() });
  return normalizeFoodRow({ name: trimmed, quantity: '' });
}

function ingredientToFoodRow(ing: PDFIngredient): PDFFoodRow {
  if (ing.originalString?.trim()) {
    return parseIngredientString(ing.originalString);
  }
  const record = ing as PDFIngredient & {
    servingId?: string;
    servingSize?: string;
    amount?: number;
    amountGrams?: number;
  };
  const name = cleanIngredientName(ing.name || '');
  let unit = String(ing.unit ?? record.servingId ?? record.servingSize ?? '').trim();
  if (!unit && record.amountGrams != null && record.amountGrams > 0) {
    unit = 'g';
  }
  let qtyNum = ing.quantity ?? record.amount ?? ing.amount;
  if (
    (qtyNum == null || Number.isNaN(Number(qtyNum))) &&
    unit === 'g' &&
    record.amountGrams != null &&
    record.amountGrams > 0
  ) {
    qtyNum = record.amountGrams;
  }
  const quantity = formatIngredientAmountAndUnit(
    qtyNum != null && !Number.isNaN(Number(qtyNum)) ? Number(qtyNum) : undefined,
    unit,
  );
  return { name: name || 'Item', quantity };
}

function buildFoodRows(recipe: PDFRecipe): PDFFoodRow[] {
  if (recipe.foodRows?.length) return recipe.foodRows.map(normalizeFoodRow);
  if (recipe.ingredients?.length) {
    return recipe.ingredients.map(ingredientToFoodRow).filter((row) => row.name || row.quantity);
  }
  if (recipe.ingredientLines?.length) {
    return recipe.ingredientLines.map(parseIngredientString).filter((row) => row.name || row.quantity);
  }
  return [];
}

function formatFoodRowInline(row: PDFFoodRow): string {
  return [row.quantity.trim(), row.name.trim()].filter(Boolean).join(' ').trim();
}

function buildIngredientSummaryRows(recipe: PDFRecipe): PDFFoodRow[] {
  const text = buildFoodRows(recipe)
    .map(formatFoodRowInline)
    .filter(Boolean)
    .join(', ');

  return text ? [{ name: text, quantity: '' }] : [];
}

function numberFromPdfValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/,/g, '').replace(/[^\d.-]/g, '');
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function firstPdfNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const parsed = numberFromPdfValue(value);
    if (parsed != null) return parsed;
  }
  return null;
}

function normalizeRecipeNutritionSummary(
  summary: RecipePdfNutritionSummary | null | undefined,
): RecipePdfNutritionSummary | undefined {
  if (!summary) return undefined;
  const normalized: RecipePdfNutritionSummary = {
    calories: firstPdfNumber(summary.calories),
    carbs: firstPdfNumber(summary.carbs),
    protein: firstPdfNumber(summary.protein),
    fat: firstPdfNumber(summary.fat),
  };

  const hasValue = Object.values(normalized).some((value) => value != null && Number(value) > 0);
  return hasValue ? normalized : undefined;
}

function extractRecipeNutritionSummary(record: Record<string, unknown>): RecipePdfNutritionSummary | undefined {
  const nutrition =
    record.nutrition && typeof record.nutrition === 'object' && !Array.isArray(record.nutrition)
      ? (record.nutrition as Record<string, unknown>)
      : {};
  const macros =
    nutrition.macros && typeof nutrition.macros === 'object' && !Array.isArray(nutrition.macros)
      ? (nutrition.macros as Record<string, unknown>)
      : {};
  const nutritionDetails =
    record.nutritionDetails && typeof record.nutritionDetails === 'object' && !Array.isArray(record.nutritionDetails)
      ? (record.nutritionDetails as Record<string, unknown>)
      : {};
  const nutritionDetailsMacros =
    nutritionDetails.macros && typeof nutritionDetails.macros === 'object' && !Array.isArray(nutritionDetails.macros)
      ? (nutritionDetails.macros as Record<string, unknown>)
      : {};

  const quantity = (value: unknown): number | null => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return firstPdfNumber((value as Record<string, unknown>).quantity, (value as Record<string, unknown>).value);
    }
    return firstPdfNumber(value);
  };

  return normalizeRecipeNutritionSummary({
    calories: firstPdfNumber(
      record.totalCalories,
      record.calories,
      record.kcal,
      nutrition.calories,
      quantity(nutritionDetails.calories),
    ),
    carbs: firstPdfNumber(
      record.carbs,
      record.carbohydrate,
      record.totalCarbs,
      nutrition.carbs,
      nutrition.carbohydrate,
      macros.carbs,
      macros.carbohydrate,
      quantity(nutritionDetailsMacros.carbs),
      quantity(nutritionDetailsMacros.carbohydrate),
    ),
    protein: firstPdfNumber(
      record.protein,
      record.totalProtein,
      nutrition.protein,
      macros.protein,
      quantity(nutritionDetailsMacros.protein),
    ),
    fat: firstPdfNumber(
      record.fat,
      record.totalFat,
      record.total_fat,
      nutrition.fat,
      nutrition.total_fat,
      macros.fat,
      macros.total_fat,
      quantity(nutritionDetailsMacros.fat),
      quantity(nutritionDetailsMacros.total_fat),
    ),
  });
}

function formatPdfMacroNumber(value: number | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function recipeMacroPercentages(summary: RecipePdfNutritionSummary): {
  carbsPct: number;
  proteinPct: number;
  fatPct: number;
} {
  const carbs = Math.max(0, Number(summary.carbs ?? 0));
  const protein = Math.max(0, Number(summary.protein ?? 0));
  const fat = Math.max(0, Number(summary.fat ?? 0));
  const carbsKcal = carbs * 4;
  const proteinKcal = protein * 4;
  const fatKcal = fat * 9;
  const totalMacroKcal = carbsKcal + proteinKcal + fatKcal;

  if (totalMacroKcal <= 0) {
    return { carbsPct: 0, proteinPct: 0, fatPct: 0 };
  }

  return {
    carbsPct: (carbsKcal / totalMacroKcal) * 100,
    proteinPct: (proteinKcal / totalMacroKcal) * 100,
    fatPct: (fatKcal / totalMacroKcal) * 100,
  };
}

/** Skip recipe blocks when the meal plan payload has no displayable recipe fields. */
function recipeHasVisibleContent(recipe: PDFRecipe, includeInstructions: boolean): boolean {
  const name = (recipe.recipeName || '').trim();
  if (name && name !== 'Item') return true;
  if (recipe.servingLabel?.trim()) return true;
  if (buildFoodRows(recipe).length > 0) return true;
  if (normalizeRecipeNutritionSummary(recipe.nutritionSummary)) return true;
  return includeInstructions && getInstructionPoints(recipe).length > 0;
}

function buildServingLabel(record: Record<string, unknown>): string | undefined {
  const itemType = String(record.itemType ?? '').trim().toLowerCase();
  const recipeId = String(record.recipeId ?? record.id ?? '').trim();
  const isRecipe = itemType ? itemType === 'recipe' : recipeId.length > 0;

  if (isRecipe) {
    const servings = Number(record.nutritionServings ?? record.servings ?? 1);
    const servingSizes = Array.isArray(record.servingSizes)
      ? (record.servingSizes as Array<{ label?: string; servingId?: string; isDefault?: boolean }>)
      : [];
    const selectedId = String(record.selectedServingId ?? record.defaultServingId ?? '').trim();
    const selected =
      servingSizes.find((entry) => String(entry.servingId ?? '').trim() === selectedId) ??
      servingSizes.find((entry) => entry.isDefault) ??
      servingSizes[0];
    const label = String(selected?.label ?? selectedId ?? '').trim();
    if (label) return label;
    if (Number.isFinite(servings) && servings > 0) {
      return `${formatQty(servings)} Serving${servings > 1 ? 's' : ''}`;
    }
    return undefined;
  }

  const amount = Number(record.amount ?? record.nutritionServings ?? record.servings ?? 1);
  const unit = String(record.servingId ?? record.unit ?? 'serving').trim();
  if (!Number.isFinite(amount)) return undefined;
  return `${formatQty(amount)} ${unit}`.trim();
}

function rasterizeLoadedImage(image: HTMLImageElement): CachedLogoImage | null {
  try {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width * 2;
    canvas.height = height * 2;
    const context = canvas.getContext('2d');
    if (!context) return null;

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL('image/png'), width, height };
  } catch {
    return null;
  }
}

function loadImageFromSource(src: string, crossOrigin = false): Promise<CachedLogoImage | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof Image === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const image = new Image();
    if (crossOrigin) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(rasterizeLoadedImage(image));
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

async function loadLogoImageData(): Promise<CachedLogoImage | null> {
  if (cachedLogoImage) return cachedLogoImage;
  if (loadingLogoImage) return loadingLogoImage;

  loadingLogoImage = loadImageFromSource(logo2Svg).then((image) => {
    if (image) cachedLogoImage = image;
    return image;
  }).finally(() => {
    loadingLogoImage = null;
  });

  return loadingLogoImage;
}

async function loadClinicLogoImageData(url: string): Promise<CachedLogoImage | null> {
  const src = url.trim();
  if (!src) return null;

  const cached = cachedClinicLogos.get(src);
  if (cached) return cached;

  const inFlight = loadingClinicLogos.get(src);
  if (inFlight) return inFlight;

  const promise = (async () => {
    if (src.startsWith('data:') || src.startsWith('blob:')) {
      return loadImageFromSource(src, false);
    }

    try {
      const response = await fetch(src, { mode: 'cors' });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (blob.type && !blob.type.startsWith('image/')) return null;
      const objectUrl = URL.createObjectURL(blob);
      try {
        return await loadImageFromSource(objectUrl, false);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      return loadImageFromSource(src, true);
    }
  })().finally(() => {
    loadingClinicLogos.delete(src);
  });

  loadingClinicLogos.set(src, promise);
  const image = await promise;
  if (image) cachedClinicLogos.set(src, image);
  return image;
}

async function resolveFreshProfilePdfBranding(): Promise<{ clinicName?: string; clinicLogoUrl?: string }> {
  if (loadingProfilePdfBranding) return loadingProfilePdfBranding;

  loadingProfilePdfBranding = (async () => {
    try {
      const response = await getNutritionistProfile();
      const root = response?.data ?? response;
      return resolveNutritionistPdfBranding(root as Record<string, unknown>);
    } catch {
      return {};
    }
  })().finally(() => {
    loadingProfilePdfBranding = null;
  });

  return loadingProfilePdfBranding;
}

async function resolveExportPdfBranding(
  exportBranding: { clinicName?: string; clinicLogoUrl?: string },
  options?: MealPlanPdfDownloadOptions,
): Promise<{ clinicName?: string; clinicLogoUrl?: string }> {
  const profileBranding = await resolveFreshProfilePdfBranding();
  return {
    clinicName:
      profileBranding.clinicName ??
      exportBranding.clinicName ??
      options?.clinicName ??
      undefined,
    clinicLogoUrl:
      profileBranding.clinicLogoUrl ??
      exportBranding.clinicLogoUrl ??
      options?.clinicLogoUrl ??
      undefined,
  };
}

/** Clinic logo → CalorieScience logo with clinic name → clinic name text → placeholder. */
async function resolvePdfBannerBranding(options: {
  clinicLogoUrl?: string;
  clinicName?: string;
  includeCalorieScienceLogo?: boolean;
}): Promise<PdfBannerBranding> {
  const clinicLogoUrl = options.clinicLogoUrl?.trim();
  const clinicName = options.clinicName?.trim();
  if (clinicLogoUrl) {
    const clinicLogo = await loadClinicLogoImageData(clinicLogoUrl);
    if (clinicLogo) return { mode: 'image', image: clinicLogo, label: clinicName };
  }

  if (options.includeCalorieScienceLogo) {
    const calorieScienceLogo = await loadLogoImageData();
    if (calorieScienceLogo) return { mode: 'image', image: calorieScienceLogo, label: clinicName };
  }

  if (clinicName) return { mode: 'text', label: clinicName };

  return { mode: 'placeholder' };
}

function toTitleCase(value: string): string {
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getMedicalConditionLabel(source: ClientPdfSource): string {
  const fromDetail = Array.isArray(source.medicalConditionDetails)
    ? source.medicalConditionDetails
        .map((condition) =>
          formatDisplayLabel(condition.displayName || condition.conditionName || condition.conditionId || ''),
        )
        .filter(Boolean)
    : [];

  const fromLegacy =
    typeof source.medicalConditions === 'string'
      ? source.medicalConditions
          .split(',')
          .map((value) => formatDisplayLabel(value))
          .filter(Boolean)
      : Array.isArray(source.medicalConditions)
        ? formatDisplayLabelList(source.medicalConditions)
        : [];

  return Array.from(new Set([...fromDetail, ...fromLegacy])).join(', ');
}

function resolveAllergyLabels(values: string[]): string[] {
  return values.map((value) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    const normalized = trimmed === 'sulfites' ? 'sulfite' : trimmed;
    return getValidAllergies().find((entry) => entry.value.toLowerCase() === normalized.toLowerCase())?.name || formatDisplayLabel(trimmed);
  });
}

function resolveIntoleranceLabels(values: string[]): string[] {
  return values.map((value) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    let normalized = trimmed;
    if (normalized === 'gluten_non_coeliac') normalized = 'gluten';
    if (normalized === 'fodmaps') normalized = 'fodmap';

    const intolerance = getValidFoodIntolerances().find(
      (entry) => entry.value.toLowerCase() === normalized.toLowerCase(),
    );
    if (intolerance) return intolerance.name;

    const preference = getValidPreferences().find((entry) => entry.value.toLowerCase() === normalized.toLowerCase());
    if (preference) return preference.name;

    return formatDisplayLabel(trimmed);
  });
}

/** Normalize API/client records into the fields used by the PDF header. */
export function normalizeClientForPdf(source: ClientPdfSource | null | undefined): ClientPDFInfo | null {
  if (!source) return null;

  const extended = source as ClientPdfSource & { occupation?: string };
  const allergiesRaw = [
    ...(Array.isArray(source.allergies) ? source.allergies : []),
    ...(Array.isArray(source.clientGoal?.allergies) ? source.clientGoal.allergies : []),
  ];
  const allergyValues = Array.from(
    new Set(
      allergiesRaw
        .map((value) => String(value).trim())
        .filter(Boolean)
        .map((value) => (value === 'sulfites' ? 'sulfite' : value)),
    ),
  );

  const intoleranceRaw = Array.isArray(source.foodIntolerances)
    ? source.foodIntolerances
    : Array.isArray(source.dietaryPreferences)
      ? source.dietaryPreferences
      : [];
  const intoleranceValues = Array.from(
    new Set(intoleranceRaw.map((value) => String(value).trim()).filter(Boolean)),
  );

  const medicalLabel = getMedicalConditionLabel(source);
  const weightKg = source.weightKg != null && !Number.isNaN(Number(source.weightKg)) ? Number(source.weightKg) : undefined;
  const heightCm = source.heightCm != null && !Number.isNaN(Number(source.heightCm)) ? Number(source.heightCm) : undefined;
  const bmiFromMetrics = source.healthMetrics?.bmi;
  const bmi =
    source.bmi != null && !Number.isNaN(Number(source.bmi))
      ? Number(source.bmi)
      : bmiFromMetrics != null && !Number.isNaN(Number(bmiFromMetrics))
        ? Number(bmiFromMetrics)
        : undefined;

  const extendedAge = (source as ClientPDFInfo & { age?: number }).age;

  return {
    fullName: source.fullName,
    firstName: source.firstName,
    lastName: source.lastName,
    dateOfBirth: source.dateOfBirth,
    age: extendedAge != null && !Number.isNaN(Number(extendedAge)) ? Number(extendedAge) : undefined,
    gender: source.gender,
    sexAtBirth: source.sexAtBirth?.trim() || undefined,
    occupation: extended.occupation?.trim() || undefined,
    weightKg,
    heightCm,
    bmi,
    preferredMeasurementSystem: source.preferredMeasurementSystem,
    medicalConditions: medicalLabel || undefined,
    allergies: resolveAllergyLabels(allergyValues).filter(Boolean),
    foodIntolerances: resolveIntoleranceLabels(intoleranceValues).filter(Boolean),
    clientGoal: source.clientGoal,
  };
}

export function clientToPdfInfo(client: ClientPdfSource | null | undefined): ClientPDFInfo | null {
  return normalizeClientForPdf(client);
}

function drawWrappedLines(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const lines = wrapText(doc, text, maxWidth);
  doc.setFont('helvetica', 'normal');
  setTextColor(doc, COLORS.textBody);
  for (const line of lines) {
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

function formatPdfWeight(kg: number, system: 'metric' | 'imperial'): string {
  const kgNum = Number(kg);
  if (Number.isNaN(kgNum) || kgNum <= 0) return '';
  if (system === 'imperial') {
    return formatWeight(kgNum, system);
  }
  const rounded = Math.abs(kgNum - Math.round(kgNum)) < 0.05 ? String(Math.round(kgNum)) : kgNum.toFixed(1);
  return `${rounded}kg`;
}

function formatPdfHeight(cm: number, system: 'metric' | 'imperial'): string {
  const cmNum = Number(cm);
  if (Number.isNaN(cmNum) || cmNum <= 0) return '';
  if (system === 'imperial') {
    return formatHeight(cmNum, system);
  }
  return `${Math.round(cmNum)} cm`;
}

/** Draw meta row segments inline with tight gaps (no fixed column spacing). */
function drawInlineMetaSegments(
  doc: jsPDF,
  segments: string[],
  startX: number,
  y: number,
  maxWidth: number,
): void {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(CLIENT_META_FONT_SIZE);
  setTextColor(doc, COLORS.textBody);

  let x = startX;
  const rightEdge = startX + maxWidth;

  for (const segment of segments) {
    const trimmed = segment.trim();
    if (!trimmed) continue;

    const segmentWidth = doc.getTextWidth(trimmed);
    if (x > startX && x + segmentWidth > rightEdge) break;

    doc.text(trimmed, x, y);
    x += segmentWidth + CLIENT_META_SEGMENT_GAP;
  }
}

function toDisplayLabel(value: string): string {
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatGenderDisplayForPdf(value?: string | null): string {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return '';

  const genderOption = GENDER_OPTIONS.find((option) => option.value === normalized);
  if (genderOption) return genderOption.label;

  const sexAtBirthOption = SEX_AT_BIRTH_OPTIONS.find((option) => option.value === normalized);
  if (sexAtBirthOption) return sexAtBirthOption.label;

  return toDisplayLabel(normalized);
}

function isBinaryGender(value?: string | null): boolean {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'male' || normalized === 'female';
}

/** Age/gender/occupation row — uses sex at birth when gender is not male/female. */
function buildClientProfileLabel(client: ClientPDFInfo): string {
  const parts: string[] = [];
  if (client.age != null && !Number.isNaN(Number(client.age))) {
    parts.push(`${Math.round(Number(client.age))} yrs`);
  } else if (client.dateOfBirth) {
    try {
      const birthDate = new Date(client.dateOfBirth);
      parts.push(`${differenceInYears(new Date(), birthDate)} yrs`);
    } catch {
      /* ignore */
    }
  }

  const genderRaw = client.gender?.trim();
  const sexAtBirthRaw = client.sexAtBirth?.trim();
  if (genderRaw) {
    if (isBinaryGender(genderRaw)) {
      parts.push(formatGenderDisplayForPdf(genderRaw));
    } else if (sexAtBirthRaw) {
      parts.push(formatGenderDisplayForPdf(sexAtBirthRaw));
    } else {
      parts.push(formatGenderDisplayForPdf(genderRaw));
    }
  } else if (sexAtBirthRaw) {
    parts.push(formatGenderDisplayForPdf(sexAtBirthRaw));
  }

  if (client.occupation?.trim()) {
    parts.push(client.occupation.trim());
  }
  return parts.join(', ');
}

function buildClientVitalsLabels(client: ClientPDFInfo): {
  profile: string;
  weight: string;
  height: string;
  bmi: string;
} {
  const system = (client.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';
  const weightText =
    client.weightKg != null && client.weightKg > 0 ? formatPdfWeight(client.weightKg, system) : '';
  const heightText =
    client.heightCm != null && client.heightCm > 0 ? formatPdfHeight(client.heightCm, system) : '';

  let bmiText = '';
  if (client.weightKg && client.heightCm) {
    const heightInMeters = client.heightCm / 100;
    bmiText = `${(client.weightKg / (heightInMeters * heightInMeters)).toFixed(1)} kg/m\u00B2`;
  } else if (client.bmi != null && !Number.isNaN(Number(client.bmi))) {
    bmiText = `${Number(client.bmi).toFixed(1)} kg/m\u00B2`;
  }

  return {
    profile: buildClientProfileLabel(client),
    weight: weightText ? `Weight: ${weightText}` : '',
    height: heightText ? `Height: ${heightText}` : '',
    bmi: bmiText ? `BMI: ${bmiText}` : '',
  };
}

function buildClientMedicalLabels(client: ClientPDFInfo): {
  medical: string;
  allergies: string;
  intolerances: string;
} {
  const medical =
    typeof client.medicalConditions === 'string'
      ? formatDelimitedDisplayLabel(client.medicalConditions)
      : Array.isArray(client.medicalConditions) && client.medicalConditions.length > 0
        ? formatDisplayLabelList(client.medicalConditions).join(', ')
        : '';
  const allergies =
    Array.isArray(client.allergies) && client.allergies.length > 0 ? formatDisplayLabelList(client.allergies).join(', ') : '';
  const intolerances =
    Array.isArray(client.foodIntolerances) && client.foodIntolerances.length > 0
      ? formatDisplayLabelList(client.foodIntolerances).join(', ')
      : '';

  return {
    medical: medical ? `Medical Condition: ${medical}` : '',
    allergies: allergies ? `Allergies: ${allergies}` : '',
    intolerances: intolerances ? `Intolerances: ${intolerances}` : '',
  };
}

/** Client block below the blue banner — matches reference PDF column layout. */
function renderClientDetailsBlock(
  doc: jsPDF,
  startY: number,
  clientDetails: ClientPDFInfo,
  planDateLabel: string,
  contentWidth: number,
): number {
  let currentY = startY;
  const x0 = MARGIN;

  const clientName =
    clientDetails.fullName ||
    [clientDetails.firstName, clientDetails.lastName].filter(Boolean).join(' ') ||
    '';

  if (clientName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(CLIENT_NAME_FONT_SIZE_PT);
    setTextColor(doc, CLIENT_NAME_COLOR);
    doc.text(clientName, x0, currentY);
  }

  if (planDateLabel) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(CLIENT_DATE_FONT_SIZE);
    setTextColor(doc, COLORS.textBody);
    const dateWidth = doc.getTextWidth(planDateLabel);
    doc.text(planDateLabel, MARGIN + contentWidth - dateWidth, currentY);
  }

  currentY += CLIENT_NAME_LINE_HEIGHT_MM;

  const vitals = buildClientVitalsLabels(clientDetails);
  const vitalsSegments = [vitals.profile, vitals.weight, vitals.height, vitals.bmi].filter(Boolean);
  if (vitalsSegments.length > 0) {
    drawInlineMetaSegments(doc, vitalsSegments, x0, currentY, contentWidth);
    currentY += CLIENT_META_ROW_GAP;
  }

  const medical = buildClientMedicalLabels(clientDetails);
  const medicalSegments = [medical.medical, medical.allergies, medical.intolerances].filter(Boolean);
  if (medicalSegments.length > 0) {
    drawInlineMetaSegments(doc, medicalSegments, x0, currentY, contentWidth);
    currentY += CLIENT_META_ROW_GAP;
  }

  return currentY;
}


// ============================================================================
// PDF rendering
// ============================================================================
type ColumnState = {
  x: number;
  y: number;
  /** Full column left edge (for day title bar). */
  columnX: number;
  columnWidth: number;
};

type DayBorderSegment = 'full' | 'top' | 'middle' | 'bottom';

type LayoutBlock = {
  height: number;
  render: (doc: jsPDF, col: ColumnState) => void;
  wrapInDayBorder?: boolean;
  dayBorderSegment?: DayBorderSegment;
  wrapInChoiceBorder?: boolean;
  choiceBorderSegment?: DayBorderSegment;
  /** Layout-only spacing; do not render a bordered column if this is all the column has. */
  isSpacer?: boolean;
  /** True for the "Day N" title bar — only rendered once at the start of each day column. */
  isDayHeader?: boolean;
};

type PageLayout = {
  showHeader: boolean;
  leftBlocks: LayoutBlock[];
  rightBlocks: LayoutBlock[];
  leftContinuesFromPrev: boolean;
  rightContinuesFromPrev: boolean;
};

const CONTINUATION_START_Y = MARGIN + px(56);
/** Extra top inset when a day column continues from the previous page (avoids title/border overlap). */
const CONTINUATION_TOP_PADDING = px(28);
/** Max height of a single layout block (continuation page column). */
const MAX_COLUMN_BLOCK_HEIGHT = CONTENT_BOTTOM - CONTINUATION_START_Y;
const INSTRUCTION_LINE_HEIGHT = px(15);
const INSTRUCTION_LABEL_HEIGHT = px(20);

function normalizeInstructionStep(step: string): string {
  return step
    .trim()
    .replace(/^\d+\.\s*/, '')
    .replace(/^step\s+\d+\s*:\s*/i, '')
    .trim();
}

function wrapInstructionStep(doc: jsPDF, step: string, colWidth: number): string[] {
  return wrapText(doc, normalizeInstructionStep(step), colWidth - 2);
}

function splitInstructionText(step: string): string[] {
  const normalized = normalizeInstructionStep(step)
    .replace(/\s+/g, ' ')
    .replace(/([a-z0-9)])\.([A-Z])/g, '$1. $2')
    .trim();

  if (!normalized) return [];

  return normalized
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function getInstructionPoints(recipe: PDFRecipe): string[] {
  return (recipe.instructions ?? []).flatMap(splitInstructionText);
}

function wrapInstructionPoint(doc: jsPDF, point: string, colWidth: number): string[] {
  return wrapText(doc, point, colWidth - INSTRUCTION_NUMBER_WIDTH - 2);
}

/** Solid fill for the day title band — flat bottom, rounded top matching the day border. */
function fillDayHeaderBackground(
  doc: jsPDF,
  x: number,
  top: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  setFillColor(doc, COLORS.dayBg);

  if (height <= r * 2) {
    doc.roundedRect(x, top, width, height, r, r, 'F');
    return;
  }

  doc.rect(x, top + r, width, height - r, 'F');
  doc.rect(x + r, top, width - 2 * r, r, 'F');
  doc.circle(x + r, top + r, r, 'F');
  doc.circle(x + width - r, top + r, r, 'F');
}

function renderDayTitleHeader(doc: jsPDF, col: ColumnState, dayNumber: number, dayName?: string | null): void {
  const boxTop = col.y;

  fillDayHeaderBackground(
    doc,
    col.columnX,
    boxTop,
    col.columnWidth,
    DAY_HEADER_HEIGHT,
    DAY_CONTAINER_RADIUS,
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  setTextColor(doc, COLORS.textDark);
  const title = dayName?.trim() || `Day ${dayNumber}`;
  doc.text(title, col.columnX + DAY_PADDING, boxTop + DAY_HEADER_TEXT_Y);

  setDrawColor(doc, COLORS.dayContainerBorder);
  doc.setLineWidth(1);
  doc.line(
    col.columnX,
    boxTop + DAY_HEADER_HEIGHT,
    col.columnX + col.columnWidth,
    boxTop + DAY_HEADER_HEIGHT,
  );

  col.y = boxTop + DAY_HEADER_HEIGHT;
}

function drawDayBorderOutline(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  segment: DayBorderSegment = 'full',
): void {
  if (height <= 0.5) return;

  setDrawColor(doc, COLORS.dayContainerBorder);
  doc.setLineWidth(DAY_BORDER_LINE_WIDTH);

  if (segment === 'full' || segment === 'top') {
    doc.roundedRect(x, y, width, height, DAY_CONTAINER_RADIUS, DAY_CONTAINER_RADIUS, 'S');
    return;
  }

  // Continued days still need a visible page-local top edge; otherwise the card
  // looks chopped open at the page break.
  const xRight = x + width;
  const yBottom = y + height;
  doc.line(x, y, xRight, y);
  doc.line(x, y, x, yBottom);
  doc.line(xRight, y, xRight, yBottom);
  doc.line(x, yBottom, xRight, yBottom);
}

/** Solid fill for the meal-choice header — flat bottom, rounded top matching the choice card. */
function fillChoiceHeaderBackground(
  doc: jsPDF,
  x: number,
  top: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  setFillColor(doc, CHOICE_HEADER_BG);

  if (height <= r * 2) {
    doc.roundedRect(x, top, width, height, r, r, 'F');
    return;
  }

  doc.rect(x, top + r, width, height - r, 'F');
  doc.rect(x + r, top, width - 2 * r, r, 'F');
  doc.circle(x + r, top + r, r, 'F');
  doc.circle(x + width - r, top + r, r, 'F');
}

function drawChoiceBorderOutline(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  segment: DayBorderSegment = 'full',
): void {
  if (height <= 0.5) return;

  setDrawColor(doc, CHOICE_HEADER_BORDER);
  doc.setLineWidth(0.75);

  if (segment === 'full' || segment === 'top') {
    doc.roundedRect(x, y, width, height, CHOICE_CONTAINER_RADIUS, CHOICE_CONTAINER_RADIUS, 'S');
    return;
  }

  const xRight = x + width;
  const yBottom = y + height;
  doc.line(x, y, xRight, y);
  doc.line(x, y, x, yBottom);
  doc.line(xRight, y, xRight, yBottom);
  doc.line(x, yBottom, xRight, yBottom);
}

function assignDayBorderSegments(
  blocks: LayoutBlock[],
  continuesFromPrevPage: boolean,
  continuesOnNextPage: boolean,
): void {
  const borderIndices = blocks
    .map((block, index) => (block.wrapInDayBorder ? index : -1))
    .filter((index) => index >= 0);
  if (borderIndices.length === 0) return;

  const first = borderIndices[0];
  const last = borderIndices[borderIndices.length - 1];

  let segment: DayBorderSegment;
  if (!continuesFromPrevPage && !continuesOnNextPage) segment = 'full';
  else if (!continuesFromPrevPage && continuesOnNextPage) segment = 'top';
  else if (continuesFromPrevPage && !continuesOnNextPage) segment = 'bottom';
  else segment = 'middle';

  for (let i = first; i <= last; i++) {
    if (blocks[i].wrapInDayBorder) {
      blocks[i].dayBorderSegment = segment;
    }
  }
}

function assignChoiceBorderSegments(
  blocks: LayoutBlock[],
  continuesFromPrevPage: boolean,
  continuesOnNextPage: boolean,
): void {
  const regions: Array<{ start: number; end: number }> = [];
  let regionStart: number | null = null;

  blocks.forEach((block, index) => {
    if (block.wrapInChoiceBorder) {
      if (regionStart === null) regionStart = index;
    } else if (regionStart !== null) {
      regions.push({ start: regionStart, end: index - 1 });
      regionStart = null;
    }
  });
  if (regionStart !== null) {
    regions.push({ start: regionStart, end: blocks.length - 1 });
  }

  regions.forEach((region) => {
    const regionContinuesFromPrev = continuesFromPrevPage && region.start === 0;
    const regionContinuesOnNext = continuesOnNextPage && region.end === blocks.length - 1;

    let segment: DayBorderSegment;
    if (!regionContinuesFromPrev && !regionContinuesOnNext) segment = 'full';
    else if (!regionContinuesFromPrev && regionContinuesOnNext) segment = 'top';
    else if (regionContinuesFromPrev && !regionContinuesOnNext) segment = 'bottom';
    else segment = 'middle';

    for (let i = region.start; i <= region.end; i++) {
      if (blocks[i].wrapInChoiceBorder) {
        blocks[i].choiceBorderSegment = segment;
      }
    }
  });
}

function renderColumnBlocks(doc: jsPDF, col: ColumnState, blocks: LayoutBlock[]): void {
  let simY = col.y;
  const regions: Array<{ startY: number; endY: number; segment: DayBorderSegment }> = [];
  const choiceRegions: Array<{ startY: number; endY: number; segment: DayBorderSegment }> = [];
  let regionStart: number | null = null;
  let regionSegment: DayBorderSegment = 'full';
  let choiceRegionStart: number | null = null;
  let choiceRegionSegment: DayBorderSegment = 'full';

  for (const block of blocks) {
    if (block.wrapInDayBorder) {
      if (regionStart === null) {
        regionStart = simY;
        regionSegment = block.dayBorderSegment ?? 'full';
      }
    } else if (regionStart !== null) {
      regions.push({ startY: regionStart, endY: simY, segment: regionSegment });
      regionStart = null;
    }

    if (block.wrapInChoiceBorder) {
      if (choiceRegionStart === null) {
        choiceRegionStart = simY;
        choiceRegionSegment = block.choiceBorderSegment ?? 'full';
      }
    } else if (choiceRegionStart !== null) {
      choiceRegions.push({ startY: choiceRegionStart, endY: simY, segment: choiceRegionSegment });
      choiceRegionStart = null;
    }

    simY += block.height;
  }
  if (regionStart !== null) {
    regions.push({ startY: regionStart, endY: simY, segment: regionSegment });
  }
  if (choiceRegionStart !== null) {
    choiceRegions.push({ startY: choiceRegionStart, endY: simY, segment: choiceRegionSegment });
  }

  for (const block of blocks) {
    block.render(doc, col);
  }

  for (const region of choiceRegions) {
    drawChoiceBorderOutline(
      doc,
      col.columnX,
      region.startY,
      col.columnWidth,
      region.endY - region.startY,
      region.segment,
    );
  }

  for (const region of regions) {
    drawDayBorderOutline(
      doc,
      col.columnX,
      region.startY,
      col.columnWidth,
      region.endY - region.startY,
      region.segment,
    );
  }
}

function drawPageBackground(doc: jsPDF): void {
  setFillColor(doc, COLORS.white);
  doc.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, 'F');
}

function drawPageFooter(doc: jsPDF, pageIndex: number, totalPages: number, documentLabel = 'Meal Plan'): void {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  setTextColor(doc, COLORS.textMuted);

  const footerLabel = documentLabel.trim() || 'Meal Plan';
  doc.text(
    footerLabel === 'Meal Plan' ? 'CalorieScience — Meal Plan' : `CalorieScience - ${footerLabel}`,
    MARGIN,
    FOOTER_Y,
  );

  const right = `Page ${pageIndex} of ${totalPages}`;
  const rightWidth = doc.getTextWidth(right);
  doc.text(right, PAGE_WIDTH - MARGIN - rightWidth, FOOTER_Y);

  setDrawColor(doc, COLORS.border);
  doc.setLineWidth(1);
  doc.line(MARGIN, FOOTER_RULE_Y, PAGE_WIDTH - MARGIN, FOOTER_RULE_Y);
}

function drawBannerPlaceholder(doc: jsPDF, x: number, y: number, size: number): void {
  setFillColor(doc, COLORS.logoPlaceholder);
  doc.roundedRect(x, y, size, size, px(8), px(8), 'F');
}

function renderTopBanner(doc: jsPDF, branding: PdfBannerBranding, documentLabel = 'Meal Plan'): void {
  setFillColor(doc, COLORS.primary);
  doc.rect(0, 0, PAGE_WIDTH, BANNER_HEIGHT, 'F');

  let bannerLabel = '';
  let bannerLabelX = MARGIN;

  if (branding.mode === 'image') {
    try {
      const aspectRatio = branding.image.width / branding.image.height;
      const maxImageSize = BANNER_LOGO_SIZE - px(4);
      const logoWidth =
        aspectRatio >= 1 ? maxImageSize : maxImageSize * aspectRatio;
      const logoHeight =
        aspectRatio >= 1 ? maxImageSize / aspectRatio : maxImageSize;
      doc.addImage(
        branding.image.dataUrl,
        'PNG',
        MARGIN + (BANNER_LOGO_SIZE - logoWidth) / 2,
        BANNER_LOGO_TOP + (BANNER_LOGO_SIZE - logoHeight) / 2,
        logoWidth,
        logoHeight,
      );
      bannerLabel = branding.label?.trim() ?? '';
      bannerLabelX = MARGIN + BANNER_LOGO_SIZE + BANNER_TEXT_GAP;
    } catch {
      drawBannerPlaceholder(doc, MARGIN, BANNER_LOGO_TOP, BANNER_LOGO_SIZE);
      bannerLabel = branding.label?.trim() ?? '';
      bannerLabelX = MARGIN + BANNER_LOGO_SIZE + BANNER_TEXT_GAP;
    }
  } else if (branding.mode === 'text') {
    bannerLabel = branding.label.trim();
  } else {
    drawBannerPlaceholder(doc, MARGIN, BANNER_LOGO_TOP, BANNER_LOGO_SIZE);
    bannerLabel = 'Clinic Name';
    bannerLabelX = MARGIN + BANNER_LOGO_SIZE + BANNER_TEXT_GAP;
  }

  if (bannerLabel) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    setTextColor(doc, COLORS.white);
    doc.text(bannerLabel, bannerLabelX, BANNER_TEXT_Y);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  setTextColor(doc, COLORS.white);
  const mealPlanText = documentLabel.trim() || 'Meal Plan';
  const mealPlanWidth = doc.getTextWidth(mealPlanText);
  doc.text(mealPlanText, PAGE_WIDTH - MARGIN - mealPlanWidth, BANNER_TEXT_Y);
}

/** Draw a horizontal rule at `lineY` and return the baseline for the next text row below it. */
function drawSectionDivider(
  doc: jsPDF,
  lineY: number,
  gapBelowLine: number = CLIENT_DIVIDER_GAP,
): number {
  setDrawColor(doc, COLORS.border);
  doc.setLineWidth(1);
  doc.line(MARGIN, lineY, PAGE_WIDTH - MARGIN, lineY);
  return lineY + gapBelowLine;
}

function renderReferenceHeader(doc: jsPDF, _y: number, options: PdfHeaderOptions): number {
  const { branding, clientDetails, planDateLabel } = options;
  const contentWidth = PAGE_WIDTH - 2 * MARGIN;

  renderTopBanner(doc, branding, options.documentLabel);

  let currentY = BANNER_HEIGHT + CLIENT_BLOCK_TOP_GAP;

  if (clientDetails) {
    currentY = renderClientDetailsBlock(doc, currentY, clientDetails, planDateLabel, contentWidth);
    currentY += CLIENT_BLOCK_BOTTOM_GAP;
  } else if (planDateLabel) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    setTextColor(doc, COLORS.textBody);
    const dateWidth = doc.getTextWidth(planDateLabel);
    doc.text(planDateLabel, MARGIN + contentWidth - dateWidth, currentY);
    currentY += CLIENT_NAME_TO_META_GAP;
  }

  const dividerY = clientDetails
    ? currentY - CLIENT_DETAILS_DIVIDER_PULLUP
    : CLIENT_FALLBACK_DIVIDER_Y;
  currentY = drawSectionDivider(doc, dividerY, CLIENT_DIVIDER_GAP);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(DIET_PLANS_FONT_SIZE);
  setTextColor(doc, COLORS.textDark);
  doc.text(options.sectionTitle?.trim() || 'Diet Plans', MARGIN, currentY);
  currentY += DIET_PLANS_BOTTOM_GAP;

  const planNotes = normalizePractitionerNote(options.planNotes);
  if (planNotes) {
    const noteCol: ColumnState = {
      x: MARGIN,
      y: currentY,
      columnX: MARGIN,
      columnWidth: contentWidth,
    };
    renderNoteBlock(doc, noteCol, contentWidth, planNotes);
    currentY = noteCol.y;
  }

  return currentY;
}

function measureMealSlotHeaderHeight(): number {
  return MEAL_SLOT_HEADER_HEIGHT + MEAL_RECIPE_TOP_OFFSET;
}

function renderMealSlotHeader(
  doc: jsPDF,
  x: number,
  y: number,
  width: number,
  meal: PDFMeal,
): number {
  const barTop = y;
  setFillColor(doc, COLORS.mealSlotHeaderBg);
  doc.roundedRect(
    x,
    barTop,
    width,
    MEAL_SLOT_HEADER_HEIGHT,
    MEAL_SLOT_HEADER_RADIUS,
    MEAL_SLOT_HEADER_RADIUS,
    'F',
  );

  const timeLabel = resolveMealTime(meal);
  const mealLabel = meal.mealLabel?.trim() || toMealLabel(meal.mealType);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  setTextColor(doc, COLORS.mealSlotHeaderText);

  if (timeLabel) {
    const dividerX = x + MEAL_SLOT_TIME_DIVIDER_X;
    doc.text(timeLabel, x + MEAL_SLOT_HEADER_PADDING_X, barTop + MEAL_SLOT_HEADER_TEXT_Y);

    setDrawColor(doc, '#9aa7ad');
    doc.setLineWidth(1);
    doc.line(
      dividerX,
      barTop + MEAL_SLOT_DIVIDER_TOP,
      dividerX,
      barTop + MEAL_SLOT_DIVIDER_BOTTOM,
    );

    doc.setFont('helvetica', 'bold');
    doc.text(mealLabel, dividerX + MEAL_SLOT_LABEL_GAP, barTop + MEAL_SLOT_HEADER_TEXT_Y);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.text(mealLabel, x + MEAL_SLOT_HEADER_PADDING_X, barTop + MEAL_SLOT_HEADER_TEXT_Y);
  }

  return barTop + MEAL_SLOT_HEADER_HEIGHT;
}

function measureMealHeaderBlockHeight(): number {
  return measureMealSlotHeaderHeight();
}

function renderMealHeaderBlock(
  doc: jsPDF,
  col: ColumnState,
  colWidth: number,
  meal: PDFMeal,
): void {
  const startY = col.y;
  renderMealSlotHeader(doc, col.x, col.y, colWidth, meal);
  col.y = startY + MEAL_SLOT_HEADER_HEIGHT + MEAL_RECIPE_TOP_OFFSET;
}

function createRecipeSeparatorBlock(colWidth: number, wrapInDayBorder = true): LayoutBlock {
  return {
    height: RECIPE_SEPARATOR_HEIGHT,
    wrapInDayBorder,
    render(doc, col) {
      const lineY = col.y + RECIPE_SEPARATOR_BEFORE_GAP;
      setDrawColor(doc, COLORS.ingredientDivider);
      doc.setLineWidth(1);
      doc.line(col.x, lineY, col.x + colWidth, lineY);
      col.y += RECIPE_SEPARATOR_HEIGHT;
    },
  };
}

function createAndChoiceDividerBlock(colWidth: number): LayoutBlock {
  return {
    height: AND_CHOICE_DIVIDER_HEIGHT,
    render(doc, col) {
      const centerY = col.y + AND_CHOICE_DIVIDER_HEIGHT / 2;
      const label = 'AND';
      const lineInset = px(18);
      const innerX = col.x + lineInset;
      const innerRight = col.x + colWidth - lineInset;
      const labelGap = px(10);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      const labelWidth = doc.getTextWidth(label);
      const labelCenterX = col.x + colWidth / 2;

      setDrawColor(doc, '#bfdbfe');
      doc.setLineWidth(0.75);
      doc.line(innerX, centerY, labelCenterX - labelWidth / 2 - labelGap, centerY);
      doc.line(labelCenterX + labelWidth / 2 + labelGap, centerY, innerRight, centerY);

      setTextColor(doc, '#1d4ed8');
      doc.text(label, labelCenterX, centerY + 2.5, { align: 'center' });

      col.y += AND_CHOICE_DIVIDER_HEIGHT;
    },
  };
}

function createChoiceSectionGapBlock(colWidth: number): LayoutBlock {
  return {
    height: CHOICE_SECTION_GAP_HEIGHT,
    render(doc, col) {
      const lineY = col.y + CHOICE_SECTION_GAP_HEIGHT / 2;

      setDrawColor(doc, '#e5e7eb');
      doc.setLineWidth(0.5);
      doc.line(col.x, lineY, col.x + colWidth, lineY);

      col.y += CHOICE_SECTION_GAP_HEIGHT;
    },
  };
}

function createOptionGroupHeaderBlock(
  label: string,
  choiceNumber: number,
  itemCount: number,
  colWidth: number,
): LayoutBlock {
  const displayLabel = label.trim() || `Meal choice ${choiceNumber}`;
  const subtitle = itemCount > 1 ? 'All items below are part of this choice' : '';
  const barHeight = subtitle ? MEAL_SLOT_HEADER_HEIGHT + px(12) : MEAL_SLOT_HEADER_HEIGHT;
  const blockHeight = barHeight + MEAL_RECIPE_TOP_OFFSET;

  return {
    height: blockHeight,
    render(doc, col) {
      const barTop = col.y;
      const titleY = barTop + MEAL_SLOT_HEADER_TEXT_Y;
      const badgeCenterX = col.x + MEAL_SLOT_HEADER_PADDING_X + CHOICE_BADGE_SIZE / 2;
      const badgeCenterY = titleY - px(1);
      const labelX = col.x + MEAL_SLOT_HEADER_PADDING_X + CHOICE_BADGE_SIZE + px(8);

      setFillColor(doc, COLORS.mealSlotHeaderBg);
      doc.roundedRect(
        col.x,
        barTop,
        colWidth,
        barHeight,
        MEAL_SLOT_HEADER_RADIUS,
        MEAL_SLOT_HEADER_RADIUS,
        'F',
      );

      setFillColor(doc, COLORS.primary);
      doc.circle(badgeCenterX, badgeCenterY, CHOICE_BADGE_SIZE / 2, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      setTextColor(doc, '#ffffff');
      doc.text(String(choiceNumber), badgeCenterX, badgeCenterY + 3, { align: 'center' });

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      setTextColor(doc, COLORS.mealSlotHeaderText);
      doc.text(displayLabel, labelX, titleY);

      if (subtitle) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        setTextColor(doc, COLORS.textLight);
        doc.text(subtitle, labelX, titleY + px(13));
      }

      col.y = barTop + blockHeight;
    },
  };
}

function groupRecipesForGenericPdf(recipes: PDFRecipe[]): PDFOptionGroup[] {
  const groups: PDFOptionGroup[] = [];
  const groupIndexById = new Map<string, number>();

  recipes.forEach((recipe) => {
    const optionGroupId = String(recipe.optionGroupId ?? '').trim();
    if (optionGroupId) {
      const existingIndex = groupIndexById.get(optionGroupId);
      if (existingIndex !== undefined) {
        groups[existingIndex].recipes.push(recipe);
        if (!groups[existingIndex].label && recipe.optionGroupLabel) {
          groups[existingIndex].label = String(recipe.optionGroupLabel).trim();
        }
        return;
      }

      groupIndexById.set(optionGroupId, groups.length);
      groups.push({
        optionGroupId,
        label: String(recipe.optionGroupLabel ?? '').trim() || `Choice ${groups.length + 1}`,
        recipes: [recipe],
      });
      return;
    }

    groups.push({
      optionGroupId: null,
      label: String(recipe.optionGroupLabel ?? '').trim() || `Choice ${groups.length + 1}`,
      recipes: [recipe],
    });
  });

  return groups.map((group, index) => ({
    ...group,
    label: group.label.trim() || `Choice ${index + 1}`,
  }));
}

function mealUsesGenericOptionGroups(recipes: PDFRecipe[]): boolean {
  if (recipes.length <= 1) return false;
  return recipes.some((recipe) => String(recipe.optionGroupId ?? '').trim());
}

function isCompactIngredientPdfItem(recipe: PDFRecipe): boolean {
  return String(recipe.itemType ?? '').trim().toLowerCase() === 'ingredient';
}

function isMajorRecipePdfItem(recipe: PDFRecipe): boolean {
  return !isCompactIngredientPdfItem(recipe);
}

function createMealGapBlock(wrapInDayBorder = true): LayoutBlock {
  return {
    height: MEAL_GAP,
    wrapInDayBorder,
    isSpacer: true,
    render(_doc, col) {
      col.y += MEAL_GAP;
    },
  };
}

function createSpacerBlock(height: number, wrapInDayBorder = true): LayoutBlock {
  return {
    height,
    wrapInDayBorder,
    isSpacer: true,
    render(_doc, col) {
      col.y += height;
    },
  };
}

function renderRecipeCore(
  doc: jsPDF,
  col: ColumnState,
  colWidth: number,
  recipe: PDFRecipe,
  addTailPadding = true,
): void {
  const name = (recipe.recipeName || 'Item').trim();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(RECIPE_NAME_FONT_SIZE_PT);
  setTextColor(doc, COLORS.textDark);
  const nameLines = wrapText(doc, name, colWidth);
  for (const line of nameLines) {
    doc.text(line, col.x, col.y);
    col.y += RECIPE_NAME_LINE_HEIGHT;
  }

  const rows = buildIngredientSummaryRows(recipe);

  if (recipe.servingLabel?.trim()) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SERVING_FONT_SIZE_PT);
    setTextColor(doc, COLORS.textMuted);
    const servingText = recipe.servingLabel.trim().toLowerCase().startsWith('serving')
      ? recipe.servingLabel.trim()
      : recipe.servingLabel.trim();
    doc.text(servingText, col.x, col.y);
    col.y += rows.length > 0 ? RECIPE_INTRO_TO_DIVIDER_GAP : SERVING_LINE_HEIGHT;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(INGREDIENT_FONT_SIZE_PT);
  setTextColor(doc, COLORS.textBody);
  if (rows.length > 0) {
    col.y += INGREDIENT_TABLE_TOP_GAP;
    setDrawColor(doc, COLORS.ingredientDivider);
    doc.setLineWidth(1);
    doc.line(col.x, col.y, col.x + colWidth, col.y);
    col.y += px(6) + fontAscenderMm(INGREDIENT_FONT_SIZE_PT);
  }
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    const qty = row.quantity.trim();
    const nameLines = wrapText(doc, row.name, qty ? colWidth * 0.62 : colWidth);

    let lastBaselineY = col.y;
    for (let lineIndex = 0; lineIndex < nameLines.length; lineIndex++) {
      lastBaselineY = col.y;
      doc.text(nameLines[lineIndex] ?? '', col.x, lastBaselineY);
      if (lineIndex === 0 && qty) {
        const qtyWidth = doc.getTextWidth(qty);
        doc.text(qty, col.x + colWidth - qtyWidth, lastBaselineY);
      }
      if (lineIndex < nameLines.length - 1) {
        col.y = lastBaselineY + INGREDIENT_WRAP_LINE_HEIGHT;
      }
    }

    const dividerY =
      lastBaselineY + fontDescenderMm(INGREDIENT_FONT_SIZE_PT) + INGREDIENT_DIVIDER_ABOVE_MM;
    setDrawColor(doc, COLORS.ingredientDivider);
    doc.setLineWidth(1);
    doc.line(col.x, dividerY, col.x + colWidth, dividerY);
    col.y =
      dividerY +
      INGREDIENT_DIVIDER_BELOW_MM +
      (rowIndex < rows.length - 1
        ? fontAscenderMm(INGREDIENT_FONT_SIZE_PT)
        : INGREDIENT_LAST_ROW_TAIL);
  }

  if (rows.length > 0) {
    col.y += INGREDIENT_BLOCK_BOTTOM_GAP;
  }

  if (addTailPadding) {
    col.y += 1.5;
  }
}

function measureRecipeCoreHeight(
  doc: jsPDF,
  colWidth: number,
  recipe: PDFRecipe,
  addTailPadding = true,
): number {
  const name = (recipe.recipeName || 'Item').trim();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(RECIPE_NAME_FONT_SIZE_PT);
  let height = wrapText(doc, name, colWidth).length * RECIPE_NAME_LINE_HEIGHT;

  if (recipe.servingLabel?.trim()) {
    height += buildIngredientSummaryRows(recipe).length > 0
      ? RECIPE_INTRO_TO_DIVIDER_GAP
      : SERVING_LINE_HEIGHT;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(INGREDIENT_FONT_SIZE_PT);
  const foodRows = buildIngredientSummaryRows(recipe);
  if (foodRows.length > 0) {
    height += INGREDIENT_TABLE_TOP_GAP + px(6) + fontAscenderMm(INGREDIENT_FONT_SIZE_PT);
  }
  for (let rowIndex = 0; rowIndex < foodRows.length; rowIndex++) {
    const row = foodRows[rowIndex];
    const nameLines = wrapText(doc, row.name, row.quantity.trim() ? colWidth * 0.62 : colWidth);
    const lineCount = Math.max(1, nameLines.length);
    height += (lineCount - 1) * INGREDIENT_WRAP_LINE_HEIGHT;
    height +=
      fontDescenderMm(INGREDIENT_FONT_SIZE_PT) +
      INGREDIENT_DIVIDER_ABOVE_MM +
      INGREDIENT_DIVIDER_BELOW_MM;
    if (rowIndex < foodRows.length - 1) {
      height += fontAscenderMm(INGREDIENT_FONT_SIZE_PT);
    } else {
      height += INGREDIENT_LAST_ROW_TAIL;
    }
  }
  if (foodRows.length > 0) {
    height += INGREDIENT_BLOCK_BOTTOM_GAP;
  }

  return addTailPadding ? height + 1.5 : height;
}

function measureInstructionLinesHeight(doc: jsPDF, colWidth: number, recipe: PDFRecipe): number {
  const points = getInstructionPoints(recipe);
  if (points.length === 0) return 0;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(INSTRUCTION_FONT_SIZE_PT);
  let height = INSTRUCTION_LABEL_HEIGHT;
  points.forEach((point, index) => {
    height += wrapInstructionPoint(doc, point, colWidth).length * INSTRUCTION_LINE_HEIGHT;
    if (index < points.length - 1) height += INSTRUCTION_POINT_GAP;
  });
  return height;
}

function renderInstructionLines(
  doc: jsPDF,
  col: ColumnState,
  colWidth: number,
  lines: string[],
  showLabel: boolean,
  addTailPadding: boolean,
): void {
  if (showLabel) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(INSTRUCTION_LABEL_FONT_SIZE_PT);
    setTextColor(doc, COLORS.textDark);
    doc.text('Instructions', col.x, col.y);
    col.y += INSTRUCTION_LABEL_HEIGHT;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(INSTRUCTION_FONT_SIZE_PT);
    setTextColor(doc, COLORS.textBody);
  }

  for (const line of lines) {
    doc.text(line, col.x, col.y);
    col.y += INSTRUCTION_LINE_HEIGHT;
  }

  if (addTailPadding) {
    col.y += 1.5;
  }
}

function buildInstructionLayoutBlocks(
  doc: jsPDF,
  recipe: PDFRecipe,
  colWidth: number,
  wrapInDayBorder = true,
): LayoutBlock[] {
  const points = getInstructionPoints(recipe);
  if (points.length === 0) return [];

  const blocks: LayoutBlock[] = [];

  points.forEach((point, pointIndex) => {
    const lines = wrapInstructionPoint(doc, point, colWidth);
    const isLast = pointIndex === points.length - 1;
    const includesLabel = pointIndex === 0;
    blocks.push({
      height:
        (includesLabel ? INSTRUCTION_SECTION_TOP_GAP + INSTRUCTION_LABEL_HEIGHT : 0) +
        lines.length * INSTRUCTION_LINE_HEIGHT +
        (isLast ? 1.5 : INSTRUCTION_POINT_GAP),
      wrapInDayBorder,
      render(pdfDoc, col) {
        if (includesLabel) {
          col.y += INSTRUCTION_SECTION_TOP_GAP;
          pdfDoc.setFont('helvetica', 'bold');
          pdfDoc.setFontSize(INSTRUCTION_LABEL_FONT_SIZE_PT);
          setTextColor(pdfDoc, COLORS.textDark);
          pdfDoc.text('Instructions', col.x, col.y);
          col.y += INSTRUCTION_LABEL_HEIGHT;
        }

        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.setFontSize(INSTRUCTION_FONT_SIZE_PT);
        setTextColor(pdfDoc, COLORS.textBody);
        const numberLabel = `${pointIndex + 1}.`;
        pdfDoc.text(numberLabel, col.x, col.y);
        for (const line of lines) {
          pdfDoc.text(line, col.x + INSTRUCTION_NUMBER_WIDTH, col.y);
          col.y += INSTRUCTION_LINE_HEIGHT;
        }
        col.y += isLast ? 1.5 : INSTRUCTION_POINT_GAP;
      },
    });
  });

  return blocks;
}

type RecipeLayoutOptions = {
  trailingGap?: number;
  /** When set, renders the meal time bar above this recipe (kept inside the day box). */
  meal?: PDFMeal;
  /** Recipe-only PDFs need to clarify that the serving count belongs to ingredients. */
  servingLabelPrefix?: string;
  wrapInDayBorder?: boolean;
};

type CompactIngredientOptions = {
  wrapInDayBorder?: boolean;
};

function buildCompactIngredientItemBlocks(
  doc: jsPDF,
  recipe: PDFRecipe,
  colWidth: number,
  options: CompactIngredientOptions = {},
): LayoutBlock[] {
  const wrapInDayBorder = options.wrapInDayBorder !== false;
  const row: PDFFoodRow = {
    name: (recipe.recipeName || 'Item').trim(),
    quantity: (recipe.servingLabel || '').trim(),
  };
  const blockHeight = measureIngredientRowBlockHeight(doc, row, colWidth, true, true, true);
  const blocks: LayoutBlock[] = [
    {
      height: blockHeight,
      wrapInDayBorder,
      render(pdfDoc, col) {
        renderIngredientRowBlock(pdfDoc, col, colWidth, row, true, true, blockHeight);
      },
    },
  ];
  return blocks;
}

function formatRecipeServingLabel(label: string | undefined, prefix?: string): string {
  const trimmed = label?.trim();
  if (!trimmed) return '';
  if (!prefix?.trim() || new RegExp(`^${prefix.trim()}\\b`, 'i').test(trimmed)) return trimmed;
  return `${prefix.trim()} ${trimmed}`;
}

function measureRecipeIntroTextHeight(
  doc: jsPDF,
  recipe: PDFRecipe,
  colWidth: number,
  textIndent = 0,
  compactBottom = false,
): number {
  const name = (recipe.recipeName || 'Item').trim();
  const textWidth = Math.max(0, colWidth - textIndent);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(RECIPE_NAME_FONT_SIZE_PT);
  const nameLineCount = Math.max(1, wrapText(doc, name, textWidth).length);
  let height = nameLineCount * RECIPE_NAME_LINE_HEIGHT;
  if (recipe.servingLabel?.trim()) {
    // Full line height leaves a large empty band under "N Servings" before the divider.
    height += compactBottom ? RECIPE_INTRO_TO_DIVIDER_GAP : SERVING_LINE_HEIGHT;
  } else if (compactBottom) {
    height -= RECIPE_NAME_LINE_HEIGHT - RECIPE_INTRO_TO_DIVIDER_GAP;
  }
  return height;
}

function measureRecipeIntroBlockHeight(
  doc: jsPDF,
  recipe: PDFRecipe,
  colWidth: number,
  hasFoodRows: boolean,
  textIndent = 0,
): number {
  const textHeight = measureRecipeIntroTextHeight(doc, recipe, colWidth, textIndent, hasFoodRows);
  if (!hasFoodRows) {
    return Math.max(textHeight + px(8), RECIPE_INTRO_ROW_MIN_HEIGHT);
  }
  // Divider follows immediately — no extra bottom padding.
  return textHeight;
}

function renderRecipeIntro(
  doc: jsPDF,
  col: ColumnState,
  colWidth: number,
  recipe: PDFRecipe,
  servingLabelPrefix?: string,
  textIndent = 0,
  compactBottom = false,
): void {
  const name = (recipe.recipeName || 'Item').trim();
  const textX = col.x + textIndent;
  const textWidth = Math.max(0, colWidth - textIndent);
  const hasServing = Boolean(recipe.servingLabel?.trim());

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(RECIPE_NAME_FONT_SIZE_PT);
  setTextColor(doc, COLORS.textDark);

  const nameLines = wrapText(doc, name, textWidth);
  for (let index = 0; index < nameLines.length; index++) {
    doc.text(nameLines[index] ?? '', textX, col.y);
    const isLastNameLine = index === nameLines.length - 1;
    if (!isLastNameLine) {
      col.y += RECIPE_NAME_LINE_HEIGHT;
      continue;
    }
    if (hasServing) {
      col.y += RECIPE_NAME_LINE_HEIGHT;
    } else {
      col.y += compactBottom ? RECIPE_INTRO_TO_DIVIDER_GAP : RECIPE_NAME_LINE_HEIGHT;
    }
  }

  if (hasServing) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SERVING_FONT_SIZE_PT);
    setTextColor(doc, COLORS.textMuted);
    doc.text(formatRecipeServingLabel(recipe.servingLabel, servingLabelPrefix), textX, col.y);
    col.y += compactBottom ? RECIPE_INTRO_TO_DIVIDER_GAP : SERVING_LINE_HEIGHT;
  }
}

function drawPdfArc(
  doc: jsPDF,
  centerX: number,
  centerY: number,
  radius: number,
  startDeg: number,
  endDeg: number,
  color: string,
  lineWidth: number,
): void {
  const span = endDeg - startDeg;
  if (span <= 0) return;

  const steps = Math.max(10, Math.ceil(span / 3));
  const points: Array<[number, number]> = [];
  for (let i = 0; i <= steps; i++) {
    const angle = ((startDeg + (span * i) / steps) * Math.PI) / 180;
    points.push([
      centerX + Math.cos(angle) * radius,
      centerY + Math.sin(angle) * radius,
    ]);
  }

  const deltas = points.slice(1).map((point, index) => [
    point[0] - points[index][0],
    point[1] - points[index][1],
  ]);

  doc.setLineWidth(lineWidth);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  setDrawColor(doc, color);
  doc.lines(deltas, points[0][0], points[0][1], [1, 1], 'S', false);
  doc.setLineCap('butt');
  doc.setLineJoin('miter');
}

function drawRecipeMacroDonut(
  doc: jsPDF,
  centerX: number,
  centerY: number,
  radius: number,
  summary: RecipePdfNutritionSummary,
): void {
  const carbsKcal = Math.max(0, Number(summary.carbs ?? 0)) * 4;
  const proteinKcal = Math.max(0, Number(summary.protein ?? 0)) * 4;
  const fatKcal = Math.max(0, Number(summary.fat ?? 0)) * 9;
  const totalKcal = carbsKcal + proteinKcal + fatKcal;
  const lineWidth = px(7.2);

  doc.setLineWidth(lineWidth);
  setDrawColor(doc, '#f3f4f6');
  doc.circle(centerX, centerY, radius, 'S');

  if (totalKcal <= 0) return;

  const segments = [
    { value: fatKcal, color: '#6366f1' },
    { value: proteinKcal, color: '#3b82f6' },
    { value: carbsKcal, color: '#5cc5da' },
  ].filter((segment) => segment.value > 0);

  const gapDeg = segments.length > 1 ? 12 : 0;
  const usableDeg = 360 - gapDeg * segments.length;
  let cursor = -90 + gapDeg / 2;

  segments.forEach((segment) => {
    const arcDeg = (segment.value / totalKcal) * usableDeg;
    drawPdfArc(doc, centerX, centerY, radius, cursor, cursor + arcDeg, segment.color, lineWidth);
    cursor += arcDeg + gapDeg;
  });
}

function renderRecipeNutritionSummaryBlock(
  doc: jsPDF,
  col: ColumnState,
  colWidth: number,
  summary: RecipePdfNutritionSummary,
): void {
  const normalized = normalizeRecipeNutritionSummary(summary);
  if (!normalized) return;

  const startY = col.y;
  const headingY = startY + NUTRITION_BLOCK_TOP_GAP + px(12);
  const cardTop = startY + NUTRITION_BLOCK_TOP_GAP + NUTRITION_HEADING_HEIGHT + NUTRITION_CARD_TOP_GAP;
  const cardX = col.x;
  const cardWidth = colWidth;
  const cardRight = cardX + cardWidth;
  const cardBottom = cardTop + NUTRITION_CARD_HEIGHT;
  const leftWidth = Math.min(px(292), Math.max(px(210), cardWidth * 0.42));
  const dividerX = cardX + leftWidth;
  const rightX = dividerX + px(32);
  const rightWidth = cardRight - rightX - px(18);
  const calories = Number(normalized.calories ?? 0);
  const { carbsPct, proteinPct, fatPct } = recipeMacroPercentages(normalized);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  setTextColor(doc, COLORS.textDark);
  doc.text('Nutrition (per consumption)', col.x, headingY);

  setFillColor(doc, COLORS.white);
  setDrawColor(doc, '#e8edf3');
  doc.setLineWidth(1);
  doc.roundedRect(cardX, cardTop, cardWidth, NUTRITION_CARD_HEIGHT, px(10), px(10), 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setTextColor(doc, COLORS.textDark);
  doc.text('Macro distribution', cardX + px(18), cardTop + px(28));

  const circleX = cardX + leftWidth / 2;
  const circleY = cardTop + px(100);
  const ringRadius = px(39);
  drawRecipeMacroDonut(doc, circleX, circleY, ringRadius, normalized);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  setTextColor(doc, '#111827');
  const caloriesText = formatPdfMacroNumber(calories);
  doc.text(caloriesText, circleX - doc.getTextWidth(caloriesText) / 2, circleY + px(1));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setTextColor(doc, COLORS.textLight);
  doc.text('kcal', circleX - doc.getTextWidth('kcal') / 2, circleY + px(16));

  setDrawColor(doc, '#edf2f7');
  doc.setLineWidth(1);
  doc.line(dividerX, cardTop + px(32), dividerX, cardBottom - px(28));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setTextColor(doc, '#94a3b8');
  doc.text('MACRONUTRIENTS', rightX, cardTop + px(24));

  const macroRowYs = [cardTop + px(56), cardTop + px(101), cardTop + px(146)];
  const macroDividerYs = [
    (macroRowYs[0] + macroRowYs[1]) / 2 + px(8),
    (macroRowYs[1] + macroRowYs[2]) / 2 + px(8),
  ];

  const drawMacroRow = (
    rowY: number,
    label: string,
    grams: number | null | undefined,
    pct: number,
    color: string,
    dividerY?: number,
  ) => {
    setFillColor(doc, color);
    doc.circle(rightX + px(4), rowY - px(3.2), px(3.4), 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    setTextColor(doc, COLORS.textBody);
    doc.text(label, rightX + px(18), rowY);

    const gramsText = `${formatPdfMacroNumber(Number(grams ?? 0))}g`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    setTextColor(doc, '#111827');
    doc.text(gramsText, cardRight - px(18) - doc.getTextWidth(gramsText), rowY);

    const pctText = `${formatPdfMacroNumber(pct)}%`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    setTextColor(doc, color);
    doc.text(pctText, rightX + px(18), rowY + px(17.5));
    doc.setFont('helvetica', 'normal');
    setTextColor(doc, '#94a3b8');
    doc.text('of macro energy', rightX + px(18) + doc.getTextWidth(pctText) + px(5), rowY + px(17.5));

    if (dividerY != null) {
      setDrawColor(doc, '#edf2f7');
      doc.setLineWidth(1);
      doc.line(rightX, dividerY, rightX + rightWidth, dividerY);
    }
  };

  drawMacroRow(macroRowYs[0], 'Carbs', normalized.carbs, carbsPct, '#5cc5da', macroDividerYs[0]);
  drawMacroRow(macroRowYs[1], 'Protein', normalized.protein, proteinPct, '#3b82f6', macroDividerYs[1]);
  drawMacroRow(macroRowYs[2], 'Fat', normalized.fat, fatPct, '#6366f1');

  col.y = startY + NUTRITION_BLOCK_HEIGHT;
}

function measureIngredientRowBlockHeight(
  doc: jsPDF,
  row: PDFFoodRow,
  colWidth: number,
  isFirst: boolean,
  isLast: boolean,
  addTailPadding: boolean,
  options?: { skipTopDivider?: boolean; skipBottomDivider?: boolean; textIndent?: number },
): number {
  const textIndent = options?.textIndent ?? 0;
  const textWidth = Math.max(0, colWidth - textIndent);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(INGREDIENT_FONT_SIZE_PT);

  const rowTextWidth = row.quantity.trim() ? textWidth * 0.62 : textWidth;
  const lineCount = Math.max(1, wrapText(doc, row.name, rowTextWidth).length);
  const textVisualHeight =
    fontAscenderMm(INGREDIENT_FONT_SIZE_PT) +
    fontDescenderMm(INGREDIENT_FONT_SIZE_PT) +
    (lineCount - 1) * INGREDIENT_WRAP_LINE_HEIGHT;
  const rowHeight = Math.max(INGREDIENT_ROW_MIN_HEIGHT, textVisualHeight + px(6));
  const skipTopDivider = options?.skipTopDivider === true;
  const skipBottomDivider = options?.skipBottomDivider === true;

  return (
    (isFirst && !skipTopDivider ? INGREDIENT_TABLE_TOP_GAP : 0) +
    rowHeight +
    (isLast && !skipBottomDivider
      ? INGREDIENT_LAST_ROW_TAIL + INGREDIENT_BLOCK_BOTTOM_GAP + (addTailPadding ? 1.5 : 0)
      : 0)
  );
}

function renderIngredientRowBlock(
  doc: jsPDF,
  col: ColumnState,
  colWidth: number,
  row: PDFFoodRow,
  isFirst: boolean,
  isLast: boolean,
  blockHeight: number,
  options?: { skipTopDivider?: boolean; skipBottomDivider?: boolean; textIndent?: number },
): void {
  const startY = col.y;
  const textIndent = options?.textIndent ?? 0;
  const textX = col.x + textIndent;
  const textWidth = Math.max(0, colWidth - textIndent);
  const skipTopDivider = options?.skipTopDivider === true;
  const skipBottomDivider = options?.skipBottomDivider === true;

  if (isFirst && !skipTopDivider) {
    col.y += INGREDIENT_TABLE_TOP_GAP;
    setDrawColor(doc, COLORS.ingredientDivider);
    doc.setLineWidth(1);
    doc.line(col.x, col.y, col.x + colWidth, col.y);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(INGREDIENT_FONT_SIZE_PT);
  setTextColor(doc, COLORS.textBody);

  const qty = row.quantity.trim();
  const nameLines = wrapText(doc, row.name, qty ? textWidth * 0.62 : textWidth);
  const rowTop = col.y;
  const lineCount = Math.max(1, nameLines.length);
  const textVisualHeight =
    fontAscenderMm(INGREDIENT_FONT_SIZE_PT) +
    fontDescenderMm(INGREDIENT_FONT_SIZE_PT) +
    (lineCount - 1) * INGREDIENT_WRAP_LINE_HEIGHT;
  const rowHeight = Math.max(INGREDIENT_ROW_MIN_HEIGHT, textVisualHeight + px(6));
  // After the table top divider, sit text near the line instead of vertically centering
  // into a tall empty band under servings.
  const firstBaselineY =
    isFirst && !skipTopDivider
      ? rowTop + px(6) + fontAscenderMm(INGREDIENT_FONT_SIZE_PT)
      : rowTop +
        (rowHeight - textVisualHeight) / 2 +
        fontAscenderMm(INGREDIENT_FONT_SIZE_PT);

  for (let lineIndex = 0; lineIndex < nameLines.length; lineIndex++) {
    const lineBaselineY = firstBaselineY + lineIndex * INGREDIENT_WRAP_LINE_HEIGHT;
    doc.text(nameLines[lineIndex] ?? '', textX, lineBaselineY);
    if (lineIndex === 0 && qty) {
      const qtyWidth = doc.getTextWidth(qty);
      doc.text(qty, col.x + colWidth - qtyWidth, lineBaselineY);
    }
  }

  if (!skipBottomDivider) {
    const dividerY = rowTop + rowHeight;
    setDrawColor(doc, COLORS.ingredientDivider);
    doc.setLineWidth(1);
    doc.line(col.x, dividerY, col.x + colWidth, dividerY);
  }
  col.y = startY + blockHeight;
}

function buildRecipeLayoutBlocks(
  doc: jsPDF,
  recipe: PDFRecipe,
  colWidth: number,
  includeInstructions: boolean,
  options: RecipeLayoutOptions = {},
): LayoutBlock[] {
  const trailingGap = options.trailingGap ?? 0;
  const wrapInDayBorder = options.wrapInDayBorder !== false;
  const hasInstructions = includeInstructions && getInstructionPoints(recipe).length > 0;
  const markBlock = (block: LayoutBlock): LayoutBlock => ({
    ...block,
    wrapInDayBorder,
  });
  const blocks: LayoutBlock[] = [];
  const foodRows = buildIngredientSummaryRows(recipe);

  if (options.meal) {
    blocks.push(markBlock({
      height: measureMealHeaderBlockHeight(),
      render(pdfDoc, col) {
        renderMealHeaderBlock(pdfDoc, col, colWidth, options.meal!);
      },
    }));
  }

  blocks.push(markBlock({
    height: measureRecipeIntroBlockHeight(doc, recipe, colWidth, foodRows.length > 0),
    render(pdfDoc, col) {
      const startY = col.y;
      const hasFoodRows = foodRows.length > 0;
      const blockHeight = measureRecipeIntroBlockHeight(
        pdfDoc,
        recipe,
        colWidth,
        hasFoodRows,
      );
      renderRecipeIntro(
        pdfDoc,
        col,
        colWidth,
        recipe,
        options.servingLabelPrefix,
        0,
        hasFoodRows,
      );
      col.y = startY + blockHeight;
    },
  }));

  const nutritionSummary = recipe.suppressNutritionSummary
    ? undefined
    : normalizeRecipeNutritionSummary(recipe.nutritionSummary);
  if (nutritionSummary) {
    blocks.push(markBlock({
      height: NUTRITION_BLOCK_HEIGHT,
      render(pdfDoc, col) {
        renderRecipeNutritionSummaryBlock(pdfDoc, col, colWidth, nutritionSummary);
      },
    }));
  }

  foodRows.forEach((row, rowIndex) => {
    const isFirst = rowIndex === 0;
    const isLast = rowIndex === foodRows.length - 1;
    const blockHeight = measureIngredientRowBlockHeight(
      doc,
      row,
      colWidth,
      isFirst,
      isLast,
      !hasInstructions,
    );
    blocks.push(markBlock({
      height: blockHeight,
      render(pdfDoc, col) {
        renderIngredientRowBlock(pdfDoc, col, colWidth, row, isFirst, isLast, blockHeight);
      },
    }));
  });

  if (hasInstructions) {
    blocks.push(...buildInstructionLayoutBlocks(doc, recipe, colWidth, wrapInDayBorder));
  }

  // Recipe notes only — never show notes for compact ingredient items.
  if (isMajorRecipePdfItem(recipe)) {
    const recipeNoteBlock = createNoteLayoutBlock(doc, recipe.notes, colWidth, wrapInDayBorder);
    if (recipeNoteBlock) blocks.push(markBlock(recipeNoteBlock));
  }

  if (trailingGap > 0) {
    blocks.push(markBlock(createSpacerBlock(trailingGap, wrapInDayBorder)));
  }

  return blocks;
}

function appendMealSlotHeaderAndNotes(
  blocks: LayoutBlock[],
  doc: jsPDF,
  meal: PDFMeal,
  colWidth: number,
  wrapInDayBorder: boolean,
  markDayBorder: (block: LayoutBlock) => LayoutBlock,
): void {
  const mealNoteBlock = createNoteLayoutBlock(doc, meal.notes, colWidth, wrapInDayBorder);
  const afterHeaderGap = mealNoteBlock ? MEAL_NOTE_TOP_GAP : MEAL_RECIPE_TOP_OFFSET;

  blocks.push(
    markDayBorder({
      height: MEAL_SLOT_HEADER_HEIGHT + afterHeaderGap,
      render(pdfDoc, col) {
        const startY = col.y;
        renderMealSlotHeader(pdfDoc, col.x, col.y, colWidth, meal);
        col.y = startY + MEAL_SLOT_HEADER_HEIGHT + afterHeaderGap;
      },
    }),
  );

  if (mealNoteBlock) {
    blocks.push(markDayBorder(mealNoteBlock));
    blocks.push(markDayBorder(createSpacerBlock(MEAL_NOTE_BOTTOM_GAP, wrapInDayBorder)));
  }
}

function buildDayLayoutBlocks(
  doc: jsPDF,
  day: PDFDay,
  colWidth: number,
  includeInstructions: boolean,
  layoutOptions?: DayLayoutOptions,
): LayoutBlock[] {
  const hideDayStructure = layoutOptions?.hideDayStructure === true;
  const blocks: LayoutBlock[] = [];
  const wrapInDayBorder = true;
  const markDayBorder = (block: LayoutBlock): LayoutBlock => ({ ...block, wrapInDayBorder });
  const recipeLayoutOptions = { wrapInDayBorder };

  if (hideDayStructure) {
    blocks.push(
      markDayBorder({
        height: DAY_CONTENT_TOP_GAP,
        isSpacer: true,
        render(_pdfDoc, col) {
          col.y += DAY_CONTENT_TOP_GAP;
        },
      }),
    );
  } else {
    blocks.push(
      markDayBorder({
        height: DAY_HEADER_HEIGHT,
        isDayHeader: true,
        render(pdfDoc, col) {
          renderDayTitleHeader(pdfDoc, col, day.day, day.dayName);
        },
      }),
    );

    // Day notes sit directly under the day name, before meal slots.
    const dayNoteBlock = createNoteLayoutBlock(doc, day.notes, colWidth, wrapInDayBorder);
    if (dayNoteBlock) {
      blocks.push(markDayBorder(createSpacerBlock(DAY_NOTE_TOP_GAP, wrapInDayBorder)));
      blocks.push(markDayBorder(dayNoteBlock));
      blocks.push(markDayBorder(createSpacerBlock(DAY_NOTE_BOTTOM_GAP, wrapInDayBorder)));
    } else {
      blocks.push(
        markDayBorder({
          height: DAY_CONTENT_TOP_GAP,
          isSpacer: true,
          render(_pdfDoc, col) {
            col.y += DAY_CONTENT_TOP_GAP;
          },
        }),
      );
    }
  }

  let mealIndexInDay = 0;
  for (const meal of day.meals) {
    const recipes = meal.recipes.filter((entry) => recipeHasVisibleContent(entry, includeInstructions));
    if (recipes.length === 0) continue;

    if (mealIndexInDay > 0) {
      blocks.push(markDayBorder(createMealGapBlock(wrapInDayBorder)));
    }
    mealIndexInDay += 1;

    const useGenericOptionGroups =
      layoutOptions?.showGenericOptionGroups === true && mealUsesGenericOptionGroups(recipes);

    if (useGenericOptionGroups) {
      const optionGroups = groupRecipesForGenericPdf(recipes);
      const showChoiceHeaders = optionGroups.length > 1;

      // Meal slot header + meal notes before choice groups / recipes.
      appendMealSlotHeaderAndNotes(blocks, doc, meal, colWidth, wrapInDayBorder, markDayBorder);

      optionGroups.forEach((group, groupIndex) => {
        if (showChoiceHeaders) {
          if (groupIndex > 0) {
            blocks.push(markDayBorder(createRecipeSeparatorBlock(colWidth, wrapInDayBorder)));
          }
          blocks.push(
            markDayBorder(createOptionGroupHeaderBlock(group.label, groupIndex + 1, group.recipes.length, colWidth)),
          );
        }

        group.recipes.forEach((recipe, recipeIndex) => {
          if (recipeIndex > 0) {
            const previousRecipe = group.recipes[recipeIndex - 1];
            if (isMajorRecipePdfItem(previousRecipe) && isMajorRecipePdfItem(recipe)) {
              blocks.push(markDayBorder(createRecipeSeparatorBlock(colWidth, wrapInDayBorder)));
            } else if (isMajorRecipePdfItem(previousRecipe) && isCompactIngredientPdfItem(recipe)) {
              blocks.push(markDayBorder(createSpacerBlock(px(4), wrapInDayBorder)));
            }
          }

          if (isCompactIngredientPdfItem(recipe)) {
            blocks.push(
              ...buildCompactIngredientItemBlocks(doc, recipe, colWidth, { wrapInDayBorder }),
            );
            return;
          }

          blocks.push(
            ...buildRecipeLayoutBlocks(doc, recipe, colWidth, includeInstructions, {
              trailingGap: 0,
              wrapInDayBorder,
            }),
          );
        });
      });

      blocks.push(markDayBorder(createSpacerBlock(MEAL_SLOT_BOTTOM_GAP, wrapInDayBorder)));
      continue;
    }

    // Meal slot header + meal notes, then recipes/ingredients.
    appendMealSlotHeaderAndNotes(blocks, doc, meal, colWidth, wrapInDayBorder, markDayBorder);

    for (let recipeIndex = 0; recipeIndex < recipes.length; recipeIndex++) {
      const isLastRecipeInMeal = recipeIndex === recipes.length - 1;
      blocks.push(
        ...buildRecipeLayoutBlocks(doc, recipes[recipeIndex], colWidth, includeInstructions, {
          trailingGap: 0,
          ...recipeLayoutOptions,
        }),
      );
      if (!isLastRecipeInMeal) {
        const hasRenderedInstructions =
          includeInstructions && getInstructionPoints(recipes[recipeIndex]).length > 0;
        blocks.push(markDayBorder(
          hasRenderedInstructions
            ? createRecipeSeparatorBlock(colWidth, wrapInDayBorder)
            : createSpacerBlock(RECIPE_SEPARATOR_AFTER_GAP, wrapInDayBorder),
        ));
      }
    }

    blocks.push(markDayBorder(createSpacerBlock(MEAL_SLOT_BOTTOM_GAP, wrapInDayBorder)));
  }

  blocks.push(
    markDayBorder({
      height: DAY_PADDING,
      isSpacer: true,
      render(_pdfDoc, col) {
        col.y += DAY_PADDING;
      },
    }),
  );

  return blocks;
}

function canFitBlock(y: number, block: LayoutBlock): boolean {
  return y + block.height <= CONTENT_BOTTOM;
}

function packOneColumnBlock(queue: LayoutBlock[], y: number, target: LayoutBlock[]): number {
  const block = queue[0];
  if (!block || !canFitBlock(y, block)) return y;
  target.push(queue.shift()!);
  return y + block.height;
}

function packIndependentColumnBlocks(
  leftQueue: LayoutBlock[],
  rightQueue: LayoutBlock[],
  leftStartY: number,
  rightStartY: number,
  page: PageLayout,
): void {
  let leftY = leftStartY;
  let rightY = rightStartY;

  while (leftQueue.length > 0 || rightQueue.length > 0) {
    let progressed = false;

    const nextLeftY = packOneColumnBlock(leftQueue, leftY, page.leftBlocks);
    if (nextLeftY !== leftY) {
      leftY = nextLeftY;
      progressed = true;
    }

    const nextRightY = packOneColumnBlock(rightQueue, rightY, page.rightBlocks);
    if (nextRightY !== rightY) {
      rightY = nextRightY;
      progressed = true;
    }

    if (!progressed) break;
  }
}

function hasVisibleBlocks(blocks: LayoutBlock[]): boolean {
  return blocks.some((block) => !block.isSpacer);
}

function sumBlockHeights(blocks: LayoutBlock[]): number {
  return blocks.reduce((total, block) => total + block.height, 0);
}

function dropSpacerOnlyColumns(page: PageLayout): void {
  if (!hasVisibleBlocks(page.leftBlocks)) {
    page.leftBlocks = [];
  }

  if (!hasVisibleBlocks(page.rightBlocks)) {
    page.rightBlocks = [];
  }
}

function balancePageColumnBottoms(page: PageLayout): void {
  if (!hasVisibleBlocks(page.leftBlocks) || !hasVisibleBlocks(page.rightBlocks)) {
    return;
  }

  const leftHeight = sumBlockHeights(page.leftBlocks);
  const rightHeight = sumBlockHeights(page.rightBlocks);
  const delta = Math.abs(leftHeight - rightHeight);
  if (delta <= 0.5) return;

  const filler = createSpacerBlock(delta, true);
  if (leftHeight < rightHeight) {
    page.leftBlocks.push(filler);
  } else {
    page.rightBlocks.push(filler);
  }
}

/**
 * Day pairs (1+2, 3+4, 5+6, 7 alone…) each begin on a new page at the same Y.
 * Within a pair, left/right columns flow onto further pages as needed without a forced break.
 */
function paginateMealPlanPages(
  doc: jsPDF,
  days: PDFDay[],
  firstPageStartY: number,
  includeInstructions: boolean,
  layoutOptions?: DayLayoutOptions,
): PageLayout[] {
  const pages: PageLayout[] = [];
  let isDocumentFirstPage = true;

  for (let dayIndex = 0; dayIndex < days.length; dayIndex += 2) {
    let leftQueue = buildDayLayoutBlocks(doc, days[dayIndex], DAY_INNER_WIDTH, includeInstructions, layoutOptions);
    let rightQueue =
      days[dayIndex + 1] != null
        ? buildDayLayoutBlocks(doc, days[dayIndex + 1], DAY_INNER_WIDTH, includeInstructions, layoutOptions)
        : [];

    let leftContinuesBefore = false;
    let rightContinuesBefore = false;

    while (leftQueue.length > 0 || rightQueue.length > 0) {
      const page: PageLayout = {
        showHeader: isDocumentFirstPage,
        leftBlocks: [],
        rightBlocks: [],
        leftContinuesFromPrev: leftContinuesBefore,
        rightContinuesFromPrev: rightContinuesBefore,
      };

      const leftBefore = leftQueue.length;
      const rightBefore = rightQueue.length;
      const baseStartY = isDocumentFirstPage ? firstPageStartY : CONTINUATION_START_Y;
      let leftStartY = baseStartY;
      let rightStartY = baseStartY;

      if (!isDocumentFirstPage && leftContinuesBefore && leftQueue.length > 0) {
        page.leftBlocks.push(createSpacerBlock(CONTINUATION_TOP_PADDING, true));
        leftStartY += CONTINUATION_TOP_PADDING;
      }

      if (!isDocumentFirstPage && rightContinuesBefore && rightQueue.length > 0) {
        page.rightBlocks.push(createSpacerBlock(CONTINUATION_TOP_PADDING, true));
        rightStartY += CONTINUATION_TOP_PADDING;
      }

      packIndependentColumnBlocks(leftQueue, rightQueue, leftStartY, rightStartY, page);

      const madeProgress =
        leftQueue.length < leftBefore ||
        rightQueue.length < rightBefore ||
        page.leftBlocks.length > 0 ||
        page.rightBlocks.length > 0;

      if (!madeProgress) {
        console.warn('Meal plan PDF pagination stalled; skipping remaining blocks in column.');
        break;
      }

      const leftContinuesAfter = leftQueue.length > 0;
      const rightContinuesAfter = rightQueue.length > 0;
      dropSpacerOnlyColumns(page);
      if (page.leftBlocks.length === 0 && page.rightBlocks.length === 0) {
        leftContinuesBefore = leftContinuesAfter;
        rightContinuesBefore = rightContinuesAfter;
        continue;
      }
      balancePageColumnBottoms(page);
      assignDayBorderSegments(page.leftBlocks, leftContinuesBefore, leftContinuesAfter);
      assignDayBorderSegments(page.rightBlocks, rightContinuesBefore, rightContinuesAfter);
      leftContinuesBefore = leftContinuesAfter;
      rightContinuesBefore = rightContinuesAfter;

      pages.push(page);
      isDocumentFirstPage = false;
    }
  }

  return pages;
}

function renderLayoutPage(doc: jsPDF, page: PageLayout, headerOptions: PdfHeaderOptions): void {
  drawPageBackground(doc);

  const baseStartY = page.showHeader
    ? renderReferenceHeader(doc, MARGIN, headerOptions)
    : CONTINUATION_START_Y;

  const leftCol: ColumnState = {
    x: LEFT_COL_X + DAY_PADDING,
    y: baseStartY,
    columnX: LEFT_COL_X,
    columnWidth: COLUMN_WIDTH,
  };
  const rightCol: ColumnState = {
    x: RIGHT_COL_X + DAY_PADDING,
    y: baseStartY,
    columnX: RIGHT_COL_X,
    columnWidth: COLUMN_WIDTH,
  };

  renderColumnBlocks(doc, leftCol, page.leftBlocks);
  renderColumnBlocks(doc, rightCol, page.rightBlocks);
}

const FULL_RECIPE_COLUMN_WIDTH = PAGE_WIDTH - 2 * MARGIN;
const FULL_RECIPE_INNER_WIDTH = FULL_RECIPE_COLUMN_WIDTH - 2 * DAY_PADDING;
const RECIPE_DETAILS_TOP_PADDING = px(22);

type SingleColumnPageLayout = {
  showHeader: boolean;
  blocks: LayoutBlock[];
};

function paginateSingleDayFullWidthPages(
  doc: jsPDF,
  day: PDFDay,
  firstPageStartY: number,
  includeInstructions: boolean,
  layoutOptions?: DayLayoutOptions,
): SingleColumnPageLayout[] {
  const queue = buildDayLayoutBlocks(doc, day, FULL_RECIPE_INNER_WIDTH, includeInstructions, layoutOptions);
  const pages: SingleColumnPageLayout[] = [];
  let isDocumentFirstPage = true;
  let continuesBefore = false;

  while (queue.length > 0) {
    const page: SingleColumnPageLayout = {
      showHeader: isDocumentFirstPage,
      blocks: [],
    };
    const queueBefore = queue.length;
    const baseStartY = isDocumentFirstPage ? firstPageStartY : CONTINUATION_START_Y;
    let currentY = baseStartY;

    if (!isDocumentFirstPage && continuesBefore) {
      page.blocks.push(createSpacerBlock(CONTINUATION_TOP_PADDING, true));
      currentY += CONTINUATION_TOP_PADDING;
    }

    while (queue.length > 0) {
      const nextY = packOneColumnBlock(queue, currentY, page.blocks);
      if (nextY === currentY) break;
      currentY = nextY;
    }

    const packedAnyContent = queue.length < queueBefore;
    if (!packedAnyContent) {
      console.warn('Single-day PDF pagination stalled; skipping remaining blocks.');
      break;
    }

    const continuesAfter = queue.length > 0;
    if (hasVisibleBlocks(page.blocks)) {
      assignDayBorderSegments(page.blocks, continuesBefore, continuesAfter);
      pages.push(page);
    }

    continuesBefore = continuesAfter;
    isDocumentFirstPage = false;
  }

  return pages;
}

function paginateRecipeDetailsPages(
  doc: jsPDF,
  recipe: PDFRecipe,
  firstPageStartY: number,
): SingleColumnPageLayout[] {
  const queue = [
    createSpacerBlock(RECIPE_DETAILS_TOP_PADDING, true),
    ...buildRecipeLayoutBlocks(doc, recipe, FULL_RECIPE_INNER_WIDTH, true, {
      servingLabelPrefix: 'Ingredients for',
    }),
    createSpacerBlock(DAY_PADDING, true),
  ];
  const pages: SingleColumnPageLayout[] = [];
  let isDocumentFirstPage = true;
  let continuesBefore = false;

  while (queue.length > 0) {
    const page: SingleColumnPageLayout = {
      showHeader: isDocumentFirstPage,
      blocks: [],
    };
    const queueBefore = queue.length;
    const baseStartY = isDocumentFirstPage ? firstPageStartY : CONTINUATION_START_Y;
    let startY = baseStartY;

    if (!isDocumentFirstPage && continuesBefore) {
      page.blocks.push(createSpacerBlock(CONTINUATION_TOP_PADDING, true));
      startY += CONTINUATION_TOP_PADDING;
    }

    let currentY = startY;
    while (queue.length > 0) {
      const nextY = packOneColumnBlock(queue, currentY, page.blocks);
      if (nextY === currentY) break;
      currentY = nextY;
    }

    const packedAnyContent = queue.length < queueBefore;
    if (!packedAnyContent) {
      console.warn('Recipe PDF pagination stalled; skipping remaining blocks.');
      break;
    }

    const continuesAfter = queue.length > 0;
    if (hasVisibleBlocks(page.blocks)) {
      assignDayBorderSegments(page.blocks, continuesBefore, continuesAfter);
      pages.push(page);
    }

    continuesBefore = continuesAfter;
    isDocumentFirstPage = false;
  }

  return pages;
}

function renderSingleColumnLayoutPage(
  doc: jsPDF,
  page: SingleColumnPageLayout,
  headerOptions: PdfHeaderOptions,
): void {
  drawPageBackground(doc);

  const baseStartY = page.showHeader
    ? renderReferenceHeader(doc, MARGIN, headerOptions)
    : CONTINUATION_START_Y;

  const col: ColumnState = {
    x: MARGIN + DAY_PADDING,
    y: baseStartY,
    columnX: MARGIN,
    columnWidth: FULL_RECIPE_COLUMN_WIDTH,
  };

  renderColumnBlocks(doc, col, page.blocks);
}

function renderRecipeDetailsLayoutPage(
  doc: jsPDF,
  page: SingleColumnPageLayout,
  headerOptions: PdfHeaderOptions,
): void {
  renderSingleColumnLayoutPage(doc, page, headerOptions);
}

// ============================================================================
// Transform API data
// ============================================================================
function normalizeMealsRecord(
  meals: Record<string, unknown> | Array<Record<string, unknown>> | undefined,
): Record<string, Record<string, unknown>> {
  if (!meals) return {};
  if (Array.isArray(meals)) {
    const normalized: Record<string, Record<string, unknown>> = {};
    meals.forEach((meal, mealIndex) => {
      if (!meal || typeof meal !== 'object') return;
      const mealTypeRaw =
        (meal.mealName as string | undefined) ??
        (meal.mealType as string | undefined) ??
        `meal-${mealIndex + 1}`;
      let mealType = String(mealTypeRaw).trim() || `meal-${mealIndex + 1}`;
      
      // Ensure unique key if multiple meals have the same name/type
      const originalMealType = mealType;
      let counter = 2;
      while (normalized[mealType]) {
        mealType = `${originalMealType}-${counter}`;
        counter++;
      }
      
      normalized[mealType] = meal;
    });
    return normalized;
  }
  if (typeof meals === 'object') {
    return Object.fromEntries(
      Object.entries(meals).map(([mealType, mealData]) => [
        mealType,
        (mealData ?? {}) as Record<string, unknown>,
      ]),
    );
  }
  return {};
}

function readMealPlanItems(
  mealData: Record<string, unknown> | Array<Record<string, unknown>>,
): Array<Record<string, unknown>> {
  if (Array.isArray(mealData)) {
    return mealData.map((item) => flattenExportItemRecord(item));
  }
  const recipes = mealData.recipes;
  if (Array.isArray(recipes) && recipes.length > 0) {
    return recipes.map((item) => flattenExportItemRecord(item as Record<string, unknown>));
  }
  const items = mealData.items;
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item) => flattenExportItemRecord(item as Record<string, unknown>));
  }
  return [];
}

function asTemplateRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringFromTemplateValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function numberFromTemplateValue(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeTemplateRecipeDetails(
  itemRecord: Record<string, unknown>,
): MealPlanExportRecipeDetails | null {
  const existing = itemRecord.recipeDetails;
  if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    return existing as MealPlanExportRecipeDetails;
  }

  const ingredientsRaw = Array.isArray(itemRecord.ingredients)
    ? itemRecord.ingredients
    : [];
  const instructionsSource =
    itemRecord.instructions ??
    itemRecord.cookingInstructions ??
    itemRecord.recipe ??
    itemRecord.steps ??
    itemRecord.method;
  const instructionsRaw = Array.isArray(instructionsSource)
    ? instructionsSource
    : typeof instructionsSource === 'string'
      ? instructionsSource.split(/\r?\n+/)
      : [];

  if (ingredientsRaw.length === 0 && instructionsRaw.length === 0) {
    return null;
  }

  const ingredients = ingredientsRaw
    .map((ingredient) => {
      if (typeof ingredient === 'string') {
        const name = ingredient.trim();
        return name ? { name, amount: 0, servingId: '' } : null;
      }

      const record = asTemplateRecord(ingredient);
      const name = stringFromTemplateValue(record.name ?? record.food ?? record.text);
      if (!name) return null;

      const amount = numberFromTemplateValue(
        record.amount ?? record.quantity ?? record.qty ?? record.value,
        0,
      );
      const servingId = stringFromTemplateValue(
        record.servingId ?? record.unit ?? record.measure ?? record.servingSize,
      );
      const amountGrams = numberFromTemplateValue(
        record.amountGrams ?? record.weight ?? record.grams,
        NaN,
      );

      return {
        name,
        amount,
        servingId,
        ...(Number.isFinite(amountGrams) && amountGrams > 0 ? { amountGrams } : {}),
      };
    })
    .filter((ingredient): ingredient is MealPlanExportRecipeDetails['ingredients'][number] => ingredient !== null);

  const instructions = instructionsRaw
    .map((step, index) => {
      const record = asTemplateRecord(step);
      const instruction =
        typeof step === 'string'
          ? step.trim()
          : stringFromTemplateValue(record.instruction ?? record.step ?? record.text);
      if (!instruction) return null;

      return {
        stepNumber: numberFromTemplateValue(record.stepNumber ?? record.number, index + 1),
        instruction,
      };
    })
    .filter((step): step is MealPlanExportRecipeDetails['instructions'][number] => step !== null);

  return ingredients.length || instructions.length
    ? { ingredients, instructions }
    : null;
}

function hasRecipeDetailsContent(details: MealPlanExportRecipeDetails | null | undefined): boolean {
  return Boolean(
    details &&
    (
      (Array.isArray(details.ingredients) && details.ingredients.length > 0) ||
      (Array.isArray(details.instructions) && details.instructions.length > 0)
    ),
  );
}

function unwrapRecipeDetailsPayload(raw: unknown): Record<string, unknown> | null {
  const root = asTemplateRecord(raw);
  const data = asTemplateRecord(root.data);
  return Object.keys(data).length > 0 ? data : (Object.keys(root).length > 0 ? root : null);
}

function getRemovedCustomizedIngredientIds(customization: Record<string, unknown>): Set<string> {
  const overrides = customization.ingredientOverrides;
  if (!Array.isArray(overrides)) return new Set();

  return new Set(
    overrides
      .map((override) => asTemplateRecord(override))
      .filter((override) => stringFromTemplateValue(override.action).toLowerCase() === 'remove')
      .map((override) => stringFromTemplateValue(override.originalIngredientId))
      .filter(Boolean),
  );
}

function customizedIngredientIsRemoved(
  ingredient: Record<string, unknown>,
  removedIds: Set<string>,
): boolean {
  if (removedIds.size === 0) return false;
  const candidateIds = [
    ingredient.recipeIngredientId,
    ingredient.ingredientId,
    ingredient.id,
    ingredient._id,
  ]
    .map(stringFromTemplateValue)
    .filter(Boolean);

  return candidateIds.some((id) => removedIds.has(id));
}

function pickCustomizedRecipeIngredients(payload: Record<string, unknown>): unknown[] | undefined {
  const customization = asTemplateRecord(payload.customization);
  const recipe = asTemplateRecord(payload.recipe);
  const candidates = [
    customization.finalIngredients,
    customization.ingredients,
    payload.ingredients,
    recipe.ingredients,
    payload.ingredientLines,
    recipe.ingredientLines,
  ];
  const source = candidates.find((candidate) => Array.isArray(candidate)) as unknown[] | undefined;
  if (!source) return undefined;

  const removedIds = getRemovedCustomizedIngredientIds(customization);
  return source.filter((ingredient) => {
    if (typeof ingredient === 'string') return true;
    return !customizedIngredientIsRemoved(asTemplateRecord(ingredient), removedIds);
  });
}

function pickCustomizedRecipeInstructions(payload: Record<string, unknown>): unknown {
  const customization = asTemplateRecord(payload.customization);
  const recipe = asTemplateRecord(payload.recipe);
  const candidates = [
    customization.instructions,
    payload.instructions,
    recipe.instructions,
    payload.cookingInstructions,
    recipe.cookingInstructions,
    payload.steps,
    recipe.steps,
    payload.method,
    recipe.method,
  ];

  return candidates.find((candidate) => (
    (Array.isArray(candidate) && candidate.length > 0) ||
    (typeof candidate === 'string' && candidate.trim().length > 0)
  ));
}

function buildRecipeDetailsExportDataFromRecipePayload(
  raw: unknown,
  options: RecipePdfDownloadOptions,
): MealPlanExportForPdfData {
  const payload = unwrapRecipeDetailsPayload(raw);
  if (!payload) {
    throw new Error('Recipe details are unavailable.');
  }

  const customization = asTemplateRecord(payload.customization);
  const recipe = asTemplateRecord(payload.recipe);
  const ingredients = pickCustomizedRecipeIngredients(payload);
  const instructions = pickCustomizedRecipeInstructions(payload);
  const recipeName =
    stringFromTemplateValue(
      options.recipeName ??
      payload.recipeName ??
      payload.name ??
      payload.title ??
      recipe.recipeName ??
      recipe.name ??
      recipe.title,
    ) || 'Recipe Details';
  const recipeId = stringFromTemplateValue(
    options.recipeId ??
    payload.recipeId ??
    payload.externalRecipeId ??
    payload.sourceId ??
    recipe.recipeId ??
    recipe.externalRecipeId ??
    recipe.id ??
    payload.id,
  );
  const itemId = stringFromTemplateValue(
    options.recipeItemId ??
    payload.itemId ??
    payload.mealPlanItemId ??
    payload.lineItemId ??
    payload.id ??
    recipeId,
  );
  const servings = numberFromTemplateValue(
    customization.nutritionServings ??
    payload.nutritionServings ??
    customization.servings ??
    payload.servings ??
    recipe.servings,
    1,
  );
  const servingId = stringFromTemplateValue(
    customization.selectedServingId ??
    payload.selectedServingId ??
    customization.servingId ??
    payload.servingId ??
    recipe.servingId ??
    customization.defaultServingId ??
    payload.defaultServingId ??
    recipe.defaultServingId,
  );

  const itemRecord: Record<string, unknown> = {
    ...recipe,
    ...payload,
    id: itemId || recipeId || 'recipe',
    itemId: itemId || recipeId || 'recipe',
    recipeId,
    itemType: 'recipe',
    name: recipeName,
    recipeName,
    servings,
    nutritionServings: servings,
    servingId,
    ...(ingredients ? { ingredients } : {}),
    ...(instructions ? { instructions } : {}),
    ...(options.nutritionSummary?.calories != null ? { calories: options.nutritionSummary.calories } : {}),
    ...(options.nutritionSummary?.carbs != null ? { carbs: options.nutritionSummary.carbs } : {}),
    ...(options.nutritionSummary?.protein != null ? { protein: options.nutritionSummary.protein } : {}),
    ...(options.nutritionSummary?.fat != null ? { fat: options.nutritionSummary.fat } : {}),
  };
  const recipeDetails = normalizeTemplateRecipeDetails(itemRecord);

  return {
    name: options.planTitle?.trim() || recipeName,
    durationDays: 1,
    days: [
      {
        id: 'recipe-day-1',
        dayNumber: options.dayNumber ?? 1,
        date: new Date().toISOString().slice(0, 10),
        meals: [
          {
            id: 'recipe-meal-1',
            mealName: options.mealName?.trim() || 'Meal',
            mealType: options.mealName?.trim() || 'Meal',
            items: [
              {
                ...(itemRecord as MealPlanExportItem),
                recipeDetails,
              },
            ],
          },
        ],
      },
    ],
  };
}

async function fetchRecipeDetailsByRecipeId(recipeId: string): Promise<unknown> {
  const fetchers = [
    () => getRecipeDetails(recipeId),
    () => getCustomRecipeDetails(recipeId),
  ];
  let lastError: unknown;

  for (const fetcher of fetchers) {
    try {
      return await fetcher();
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Recipe details are unavailable.');
}

function getTemplateItemRecipeId(item: MealPlanExportItem & Record<string, unknown>): string {
  const nestedRecipe = asTemplateRecord(item.recipe);
  return stringFromTemplateValue(
    item.recipeId ??
    item.externalRecipeId ??
    item.sourceId ??
    nestedRecipe.id,
  );
}

async function fetchTemplateRecipeDetails(
  item: MealPlanExportItem & Record<string, unknown>,
  cache: Map<string, MealPlanExportRecipeDetails | null>,
): Promise<MealPlanExportRecipeDetails | null> {
  const recipeId = getTemplateItemRecipeId(item);
  if (!recipeId) return null;

  const source = stringFromTemplateValue(item.source ?? item.recipeSource ?? item.provider).toLowerCase();
  const prefersCustom = source === 'internal' || source === 'custom' || source === 'cached';
  const cacheKey = `${prefersCustom ? 'custom' : 'details'}:${recipeId}`;
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey) ?? null;
  }

  const fetchers = prefersCustom
    ? [
        () => getCustomRecipeDetails(recipeId),
        () => getRecipeDetails(recipeId),
      ]
    : [
        () => getRecipeDetails(recipeId),
        () => getCustomRecipeDetails(recipeId),
      ];

  for (const fetcher of fetchers) {
    try {
      const response = await fetcher();
      const payload = unwrapRecipeDetailsPayload(response);
      if (!payload) continue;
      const details = normalizeTemplateRecipeDetails(payload);
      if (hasRecipeDetailsContent(details)) {
        cache.set(cacheKey, details);
        return details;
      }
    } catch {
      // Try the next recipe detail endpoint; template export should still include the slot if hydration fails.
    }
  }

  cache.set(cacheKey, null);
  return null;
}

async function hydrateTemplateExportRecipeDetails(data: MealPlanExportForPdfData): Promise<MealPlanExportForPdfData> {
  const cache = new Map<string, MealPlanExportRecipeDetails | null>();
  const tasks: Promise<void>[] = [];

  for (const day of data.days ?? []) {
    for (const meal of day.meals ?? []) {
      for (const item of meal.items ?? []) {
        const itemRecord = item as MealPlanExportItem & Record<string, unknown>;
        if (String(itemRecord.itemType ?? '').trim().toLowerCase() === 'ingredient') continue;
        if (hasRecipeDetailsContent(item.recipeDetails)) continue;

        tasks.push((async () => {
          const details = await fetchTemplateRecipeDetails(itemRecord, cache);
          if (details) {
            itemRecord.recipeDetails = details;
          }
        })());
      }
    }
  }

  await Promise.all(tasks);
  return data;
}

function templateItemToExportItem(item: MealPlanTemplateItem): MealPlanExportItem & Record<string, unknown> {
  const record = item as MealPlanTemplateItem & Record<string, unknown>;
  const itemId = stringFromTemplateValue(record.itemId ?? record.id);
  const id = stringFromTemplateValue(record.id ?? itemId);
  const itemType = stringFromTemplateValue(record.itemType) || (record.ingredientId ? 'ingredient' : 'recipe');
  const name = stringFromTemplateValue(record.name ?? record.recipeName ?? record.title) || 'Item';
  const servingId = stringFromTemplateValue(record.servingId ?? record.unit);
  const servingGrams = numberFromTemplateValue(record.servingGrams ?? record.serving_grams, NaN);

  return {
    ...record,
    id,
    itemId,
    itemType,
    name,
    recipeName: record.recipeName ?? name,
    title: record.title ?? name,
    servings: numberFromTemplateValue(record.servings, 1),
    servingId,
    ...(Number.isFinite(servingGrams) && servingGrams > 0 ? { servingGrams } : {}),
    notes: (record.notes as string | null | undefined) ?? null,
    recipeDetails: normalizeTemplateRecipeDetails(record),
    suppressNutritionSummary: true,
  };
}

function templateMealToExportMeal(meal: MealPlanTemplateMeal, index: number): MealPlanExportMeal {
  const record = meal as MealPlanTemplateMeal & Record<string, unknown>;
  const id = stringFromTemplateValue(record.mealId ?? record.id) || `meal-${index + 1}`;
  const mealName = stringFromTemplateValue(record.mealName ?? record.name) || `Meal ${index + 1}`;
  const mealType = stringFromTemplateValue(record.mealType) || 'meal';

  return {
    id,
    mealName,
    mealType,
    time: stringFromTemplateValue(record.time ?? record.mealTime),
    notes: (record.notes as string | null | undefined) ?? null,
    items: Array.isArray(meal.items) ? meal.items.map(templateItemToExportItem) : [],
  };
}

function getTemplateSingleDayMeals(template: MealPlanTemplateDetails): MealPlanTemplateMeal[] {
  if (Array.isArray(template.meals) && template.meals.length > 0) {
    return template.meals;
  }

  return (template.days ?? []).flatMap((day) => (
    Array.isArray(day.meals) ? day.meals : []
  ));
}

function transformMealPlanTemplateToExportData(template: MealPlanTemplateDetails): MealPlanExportForPdfData {
  const templateMeals = getTemplateSingleDayMeals(template);
  const firstTemplateDay = template.days?.[0];
  const createdByRecord = asTemplateRecord(template.createdBy);
  const nutritionistName = stringFromTemplateValue(createdByRecord.fullName ?? createdByRecord.name);

  return {
    id: template.id,
    name: template.templateName || template.name || 'Template',
    description: template.description ?? undefined,
    startDate: template.startDate ?? undefined,
    endDate: template.endDate ?? undefined,
    durationDays: 1,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
    ...(nutritionistName ? { nutritionistDetails: { fullName: nutritionistName } } : {}),
    days: templateMeals.length > 0
      ? [
          {
            id: `${template.id}-day-1`,
            dayNumber: 1,
            date: stringFromTemplateValue(firstTemplateDay?.date ?? template.startDate),
            dayName: null,
            notes: (firstTemplateDay?.notes as string | null | undefined) ?? null,
            meals: templateMeals.map(templateMealToExportMeal),
          },
        ]
      : [],
  };
}

async function getMealPlanTemplateExportData(templateId: string): Promise<MealPlanExportForPdfData> {
  const response = await getMealPlanTemplate(templateId);
  if (!response?.success || !response.data) {
    throw new Error(response?.message || 'Unable to load template export');
  }
  return hydrateTemplateExportRecipeDetails(transformMealPlanTemplateToExportData(response.data));
}

function exportClientToPdfInfo(data: MealPlanExportForPdfData): ClientPDFInfo | null {
  const clientDetails = data.clientDetails;
  const constraints = data.clientProfileConstraints;
  if (!clientDetails && !constraints && !data.clientName) return null;

  const detailsRecord = (clientDetails ?? {}) as Record<string, unknown>;
  const constraintsRecord = (constraints ?? {}) as Record<string, unknown>;

  return normalizeClientForPdf({
    fullName: String(data.clientName ?? '').trim() || undefined,
    dateOfBirth: String(detailsRecord.dateOfBirth ?? '').trim() || undefined,
    gender: String(detailsRecord.gender ?? '').trim() || undefined,
    occupation: String(detailsRecord.occupation ?? '').trim() || undefined,
    weightKg:
      detailsRecord.weightKg != null && !Number.isNaN(Number(detailsRecord.weightKg))
        ? Number(detailsRecord.weightKg)
        : undefined,
    heightCm:
      detailsRecord.heightCm != null && !Number.isNaN(Number(detailsRecord.heightCm))
        ? Number(detailsRecord.heightCm)
        : undefined,
    bmi:
      detailsRecord.bmi != null && !Number.isNaN(Number(detailsRecord.bmi))
        ? Number(detailsRecord.bmi)
        : undefined,
    medicalConditions: detailsRecord.medicalConditions as string[] | string | undefined,
    allergies: constraintsRecord.allergies as string[] | undefined,
    foodIntolerances: constraintsRecord.foodIntolerances as string[] | undefined,
  });
}

/** Map GET /meal-plans/{id}/export payload into PDF data, client block, and banner branding. */
export function transformMealPlanExportResponse(raw: unknown): MealPlanPdfExportResult {
  const data = unwrapMealPlanExportData(raw);
  const days = Array.isArray(data.days) ? data.days : [];
  const pdfData = transformMealPlanToPDFData({
    planNotes: resolvePlanNotesFromExport(data),
    days: days.map((day, index) => ({
      day: day.dayNumber ?? index + 1,
      dayNumber: day.dayNumber,
      dayName: day.dayName,
      date: day.date,
      notes: day.notes,
      meals: normalizeMealsRecord(
        day.meals as unknown as Record<string, unknown> | Array<Record<string, unknown>>,
      ),
    })),
  });

  const clientInfo = exportClientToPdfInfo(data);
  const clientDisplayName =
    clientInfo?.fullName ||
    String(data.clientName ?? '').trim() ||
    [clientInfo?.firstName, clientInfo?.lastName].filter(Boolean).join(' ').trim() ||
    undefined;

  const nutritionist =
    data.nutritionistDetails && typeof data.nutritionistDetails === 'object'
      ? (data.nutritionistDetails as Record<string, unknown>)
      : null;
  const branding = nutritionist ? resolveNutritionistPdfBranding(nutritionist) : {};

  return {
    pdfData,
    clientInfo,
    clientDisplayName,
    branding,
    planTitle: String(data.name ?? data.title ?? '').trim() || undefined,
    planDate: String(data.createdAt ?? data.startDate ?? '').trim() || undefined,
    planNotes: pdfData.planNotes,
  };
}

export function transformMealPlanToPDFData(mealPlan: {
  planNotes?: string | null;
  days?: Array<{
    day?: number;
    dayNumber?: number;
    dayName?: string | null;
    date?: string;
    notes?: string | null;
    meals?:
      | Record<string, { recipes?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> }>
      | Array<Record<string, unknown>>;
  }>;
}): MealPlanPDFData {
  const days: PDFDay[] = [];
  const rawDays = mealPlan?.days ?? [];

  for (let i = 0; i < rawDays.length; i++) {
    const d = rawDays[i];
    const dayNum = d?.day ?? (d as { dayNumber?: number })?.dayNumber ?? i + 1;
    const mealsRecord = normalizeMealsRecord(d?.meals);
    const pdfMeals: PDFMeal[] = [];

    for (const [mealType, mealData] of Object.entries(mealsRecord)) {
      const mealRecord = mealData;
      const recipes = readMealPlanItems(mealRecord);
      if (recipes.length === 0) continue;

      const rawMealTime = String(mealRecord.mealTime ?? mealRecord.timeSlot ?? mealRecord.time ?? '').trim();
      const mealTime =
        convertTo12HourFormat(rawMealTime) ||
        MEAL_TIME_FALLBACK[normalizeMealTypeKey(mealType)] ||
        '';
      const mealLabel =
        String(mealRecord.mealName ?? mealRecord.name ?? '').trim() || toMealLabel(mealType);

      const pdfRecipes: PDFRecipe[] = recipes.map((r) => {
        const record = r as Record<string, unknown>;
        const name = (record.recipeName ?? record.title ?? record.name ?? '').toString().trim();
        const ingredients = record.ingredients as PDFIngredient[] | undefined;
        const ingredientLines = record.ingredientLines as string[] | undefined;
        const suppressNutritionSummary = record.suppressNutritionSummary === true;
        const instructionsRaw =
          record.instructions ??
          record.cookingInstructions ??
          record.recipe ??
          record.steps ??
          record.method;

        const instructions: string[] | undefined = Array.isArray(instructionsRaw)
          ? instructionsRaw
              .map((step) => {
                if (typeof step === 'string') return step.trim();
                if (step && typeof step === 'object') {
                  const text =
                    (step as { instruction?: string; step?: string; text?: string }).instruction ??
                    (step as { step?: string; text?: string }).step ??
                    (step as { text?: string }).text;
                  return typeof text === 'string' ? text.trim() : '';
                }
                return '';
              })
              .filter((step) => step.length > 0)
          : typeof instructionsRaw === 'string'
            ? instructionsRaw
                .split(/\r?\n+/)
                .map((line) => line.trim())
                .filter((line) => line.length > 0)
            : undefined;

        return {
          recipeName: name || 'Item',
          servingLabel: buildServingLabel(record),
          notes: resolveItemNotesFromRecord(record),
          ingredients: Array.isArray(ingredients) ? ingredients : undefined,
          ingredientLines: Array.isArray(ingredientLines) ? ingredientLines : undefined,
          instructions,
          nutritionSummary: suppressNutritionSummary ? undefined : extractRecipeNutritionSummary(record),
          suppressNutritionSummary,
          itemType: String(record.itemType ?? '').trim() || undefined,
          optionGroupId: String(record.optionGroupId ?? '').trim() || null,
          optionGroupLabel: String(record.optionGroupLabel ?? '').trim() || null,
        };
      });

      pdfMeals.push({
        mealType,
        mealLabel,
        mealTime,
        notes: normalizePractitionerNote(mealRecord.notes),
        recipes: pdfRecipes,
      });
    }

    if (pdfMeals.length > 0) {
      days.push({
        day: dayNum,
        date: (d?.date ?? '').toString(),
        dayName: (d as { dayName?: string | null })?.dayName ?? undefined,
        notes: normalizePractitionerNote(d?.notes),
        meals: pdfMeals,
      });
    }
  }

  return {
    days,
    planNotes: normalizePractitionerNote(mealPlan.planNotes),
  };
}

export async function generateMealPlanPDF(
  mealPlanData: MealPlanPDFData,
  clientName?: string,
  clientDetails?: ClientPDFInfo | null,
  options?: GenerateMealPlanPDFOptions,
): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: [PAGE_WIDTH, PAGE_HEIGHT],
  });
  const days = mealPlanData?.days ?? [];
  const includeInstructions = options?.includeInstructions ?? false;
  const includeClientDetails = options?.includeClientDetails ?? true;
  const includeLogo = options?.includeLogo ?? false;
  const clinicName = options?.clinicName?.trim();
  const clinicLogoUrl = options?.clinicLogoUrl?.trim();
  const planTitle = options?.planTitle?.trim() || clientName?.trim() || '';
  const planDateLabel = format(
    options?.planDate ? new Date(options.planDate) : new Date(),
    'd MMM, yyyy',
  );

  if (days.length === 0) {
    drawPageBackground(doc);
    doc.setFontSize(12);
    setTextColor(doc, COLORS.textDark);
    doc.text('No meal plan data available.', MARGIN, 30);
    const fileName = `${(clientName || 'Meal_Plan').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    return;
  }

  const branding = await resolvePdfBannerBranding({
    clinicLogoUrl,
    clinicName,
    includeCalorieScienceLogo: includeLogo,
  });
  const normalizedClientDetails = includeClientDetails
    ? normalizeClientForPdf(clientDetails ?? null)
    : null;

  const headerOptions: PdfHeaderOptions = {
    branding,
    clientDetails: normalizedClientDetails,
    planTitle: planTitle || undefined,
    planDateLabel,
    documentLabel: options?.documentLabel,
    sectionTitle: options?.sectionTitle,
    planNotes: normalizePractitionerNote(options?.planNotes ?? mealPlanData.planNotes),
  };

  const measureDoc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: [PAGE_WIDTH, PAGE_HEIGHT],
  });
  const firstPageStartY = renderReferenceHeader(measureDoc, MARGIN, headerOptions);
  const layoutOptions: DayLayoutOptions = {
    hideDayStructure: options?.hideDayStructure === true,
    showGenericOptionGroups: options?.hideDayStructure === true,
  };
  const useSingleDayFullWidth =
    options?.hideDayStructure === true || (options?.singleDayFullWidth === true && days.length === 1);

  if (useSingleDayFullWidth) {
    const pages = paginateSingleDayFullWidthPages(
      measureDoc,
      days[0],
      firstPageStartY,
      includeInstructions,
      layoutOptions,
    );
    const totalPages = Math.max(pages.length, 1);

    for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
      if (pageIndex > 0) doc.addPage();
      renderSingleColumnLayoutPage(doc, pages[pageIndex], headerOptions);
      drawPageFooter(doc, pageIndex + 1, totalPages);
    }
  } else {
    const pages = paginateMealPlanPages(measureDoc, days, firstPageStartY, includeInstructions, layoutOptions);
    const totalPages = Math.max(pages.length, 1);

    for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
      if (pageIndex > 0) doc.addPage();
      renderLayoutPage(doc, pages[pageIndex], headerOptions);
      drawPageFooter(doc, pageIndex + 1, totalPages);
    }
  }

  const safeName = (clientName || planTitle || 'Meal_Plan').replace(/\s+/g, '_');
  const fileName = `${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}

export function generateMealPlanPDFWithInstructions(
  mealPlanData: MealPlanPDFData,
  clientName?: string,
  clientDetails?: ClientPDFInfo | null,
  options?: Omit<GenerateMealPlanPDFOptions, 'includeInstructions'>,
): Promise<void> {
  return generateMealPlanPDF(mealPlanData, clientName, clientDetails, {
    ...options,
    includeInstructions: true,
  });
}

export type MealPlanPdfDownloadOptions = {
  planTitle?: string;
  planDate?: string;
  clinicName?: string;
  clinicLogoUrl?: string;
  singleDayFullWidth?: boolean;
  hideDayStructure?: boolean;
};

export type RecipePdfDownloadOptions = MealPlanPdfDownloadOptions & {
  recipeItemId?: string;
  recipeId?: string;
  recipeName?: string;
  mealName?: string;
  recipeLookupMealName?: string;
  dayNumber?: number;
  nutritionSummary?: RecipePdfNutritionSummary;
};

type RecipeExportMatch = {
  day: MealPlanExportDay;
  meal: MealPlanExportMeal;
  item: MealPlanExportItem;
  score: number;
};

function normalizeExportMatchText(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeExportMatchId(value: unknown): string {
  return String(value ?? '').trim();
}

function normalizeExportMatchMealName(value: unknown): string {
  return normalizeExportMatchText(value).replace(/[-_]/g, ' ');
}

function exportItemCandidateIds(item: MealPlanExportItem): string[] {
  const record = item as MealPlanExportItem & Record<string, unknown>;
  return [
    record.itemId,
    record.id,
    record.lineItemId,
    record.mealPlanItemId,
    record._id,
  ]
    .map(normalizeExportMatchId)
    .filter(Boolean);
}

function exportItemCandidateRecipeIds(item: MealPlanExportItem): string[] {
  const record = item as MealPlanExportItem & Record<string, unknown>;
  return [
    record.recipeId,
    record.sourceId,
    record.originalRecipeId,
    record.spoonacularId,
    record.itemId,
    record.id,
  ]
    .map(normalizeExportMatchId)
    .filter(Boolean);
}

function exportItemDisplayName(item: MealPlanExportItem): string {
  const record = item as MealPlanExportItem & Record<string, unknown>;
  return String(record.name ?? record.recipeName ?? record.title ?? '').trim();
}

function mealNameMatchesExportOption(meal: MealPlanExportMeal, mealName: string): boolean {
  if (!mealName) return true;
  const target = normalizeExportMatchMealName(mealName);
  const candidates = [
    meal.mealName,
    meal.mealType,
    (meal as MealPlanExportMeal & Record<string, unknown>).name,
  ].map(normalizeExportMatchMealName);
  return candidates.some((candidate) => candidate === target);
}

function scoreRecipeExportItem(
  day: MealPlanExportDay,
  meal: MealPlanExportMeal,
  item: MealPlanExportItem,
  options: RecipePdfDownloadOptions,
): number | null {
  if (String(item.itemType ?? '').trim().toLowerCase() === 'ingredient') return null;

  const targetItemId = normalizeExportMatchId(options.recipeItemId);
  const targetRecipeId = normalizeExportMatchId(options.recipeId);
  const targetName = normalizeExportMatchText(options.recipeName);
  const itemIds = exportItemCandidateIds(item);
  const recipeIds = exportItemCandidateRecipeIds(item);
  const itemName = normalizeExportMatchText(exportItemDisplayName(item));

  let score = 0;
  if (targetItemId && itemIds.includes(targetItemId)) {
    score = 1000;
  } else if (targetRecipeId && recipeIds.includes(targetRecipeId)) {
    score = 800;
  } else if (targetName && itemName === targetName) {
    score = 300;
  } else {
    return null;
  }

  if (options.dayNumber != null) {
    const dayMatches = Number(day.dayNumber) === Number(options.dayNumber);
    score += dayMatches ? 60 : -180;
  }

  if (options.mealName?.trim()) {
    score += mealNameMatchesExportOption(meal, options.mealName) ? 40 : -120;
  }

  return score;
}

function findRecipeExportMatch(
  data: MealPlanExportForPdfData,
  options: RecipePdfDownloadOptions,
): RecipeExportMatch | null {
  const matches: RecipeExportMatch[] = [];
  const days = Array.isArray(data.days) ? data.days : [];

  days.forEach((day) => {
    const meals = Array.isArray(day.meals) ? day.meals : [];
    meals.forEach((meal) => {
      const items = Array.isArray(meal.items) ? meal.items : [];
      items.forEach((item) => {
        const score = scoreRecipeExportItem(day, meal, item, options);
        if (score == null) return;
        matches.push({ day, meal, item, score });
      });
    });
  });

  matches.sort((a, b) => b.score - a.score);
  return matches[0] ?? null;
}

function buildRecipeOnlyExportData(raw: unknown, options: RecipePdfDownloadOptions): MealPlanExportForPdfData {
  const data = unwrapMealPlanExportData(raw);
  const match = findRecipeExportMatch(data, options);
  if (!match) {
    throw new Error('Recipe details are unavailable for this meal plan item.');
  }

  const recipeTitle = options.recipeName?.trim() || exportItemDisplayName(match.item) || 'Recipe Details';

  return {
    ...data,
    name: recipeTitle,
    durationDays: 1,
    days: [
      {
        ...match.day,
        dayNumber: match.day.dayNumber ?? options.dayNumber ?? 1,
        meals: [
          {
            ...match.meal,
            items: [match.item],
          },
        ],
      },
    ],
  };
}

function sanitizePdfFileName(value: string): string {
  const safe = value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');
  return safe || 'Recipe_Details';
}

/** Build and download a PDF from an export payload (no network). */
export async function generateMealPlanPdfFromExportRaw(
  raw: unknown,
  options?: MealPlanPdfDownloadOptions,
): Promise<void> {
  const exportPayload = transformMealPlanExportResponse(raw);
  if (exportPayload.pdfData.days.length === 0) {
    throw new Error('No meal plan days available to export.');
  }

  const planTitle = options?.planTitle?.trim() || exportPayload.planTitle || 'Meal Plan';
  const clientName = exportPayload.clientDisplayName || planTitle;
  const branding = await resolveExportPdfBranding(exportPayload.branding, options);

  await generateMealPlanPDFWithInstructions(
    exportPayload.pdfData,
    clientName,
    exportPayload.clientInfo,
    {
      clinicName: branding.clinicName,
      clinicLogoUrl: branding.clinicLogoUrl,
      planTitle,
      planDate: options?.planDate ?? exportPayload.planDate,
      planNotes: exportPayload.planNotes ?? exportPayload.pdfData.planNotes,
      includeClientDetails: Boolean(exportPayload.clientInfo),
      includeLogo: true,
      singleDayFullWidth: options?.singleDayFullWidth,
      hideDayStructure: options?.hideDayStructure,
    },
  );
}

/** Build and download a full-width recipe details PDF from a meal plan export payload. */
export async function generateRecipeDetailsPdfFromExportRaw(
  raw: unknown,
  options: RecipePdfDownloadOptions,
): Promise<void> {
  const recipeOnlyRaw = buildRecipeOnlyExportData(raw, options);
  const exportPayload = transformMealPlanExportResponse(recipeOnlyRaw);
  const day = exportPayload.pdfData.days[0];
  const meal = day?.meals[0];
  const recipe = meal?.recipes[0];
  const recipeForPdf = recipe
    ? {
        ...recipe,
        nutritionSummary: recipe.suppressNutritionSummary
          ? undefined
          : normalizeRecipeNutritionSummary(options.nutritionSummary) ?? recipe.nutritionSummary,
      }
    : undefined;

  if (!recipeForPdf || !recipeHasVisibleContent(recipeForPdf, true)) {
    throw new Error('Recipe details are unavailable for this meal plan item.');
  }

  const recipeTitle = options.recipeName?.trim() || recipeForPdf.recipeName || 'Recipe Details';
  const planDateLabel = format(
    options.planDate ? new Date(options.planDate) : new Date(),
    'd MMM, yyyy',
  );
  const resolvedBranding = await resolveExportPdfBranding(exportPayload.branding, options);
  const branding = await resolvePdfBannerBranding({
    clinicLogoUrl: resolvedBranding.clinicLogoUrl,
    clinicName: resolvedBranding.clinicName,
    includeCalorieScienceLogo: true,
  });
  const headerOptions: PdfHeaderOptions = {
    branding,
    clientDetails: exportPayload.clientInfo,
    planTitle: recipeTitle,
    planDateLabel,
    documentLabel: 'Recipe Details',
    sectionTitle: 'Recipe Details',
  };

  const measureDoc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: [PAGE_WIDTH, PAGE_HEIGHT],
  });
  const firstPageStartY = renderReferenceHeader(measureDoc, MARGIN, headerOptions);
  const pages = paginateRecipeDetailsPages(measureDoc, recipeForPdf, firstPageStartY);
  if (pages.length === 0) {
    throw new Error('Recipe details are unavailable for this meal plan item.');
  }

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: [PAGE_WIDTH, PAGE_HEIGHT],
  });
  const totalPages = Math.max(pages.length, 1);

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    if (pageIndex > 0) doc.addPage();
    renderRecipeDetailsLayoutPage(doc, pages[pageIndex], headerOptions);
    drawPageFooter(doc, pageIndex + 1, totalPages, 'Recipe Details');
  }

  const fileName = `${sanitizePdfFileName(recipeTitle)}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}

/** Fetch GET /meal-plans/{id}/export (unless `preloadedRaw` is provided) and trigger browser PDF download. */
export async function downloadMealPlanPdfFromExportApi(
  mealPlanId: string,
  options?: MealPlanPdfDownloadOptions,
  preloadedRaw?: unknown,
): Promise<void> {
  if (preloadedRaw != null) {
    await generateMealPlanPdfFromExportRaw(preloadedRaw, options);
    return;
  }

  const response = await getMealPlanExportForPdf(mealPlanId);
  if (response?.success === false) {
    throw new Error(response.message || 'Unable to load meal plan export');
  }

  await generateMealPlanPdfFromExportRaw(response?.data ?? response, options);
}

/** Fetch GET /meal-plans/generic/{id}/export and trigger browser PDF download. */
export async function downloadGenericMealPlanPdfFromExportApi(
  genericPlanId: string,
  options?: MealPlanPdfDownloadOptions,
): Promise<void> {
  const response = await getGenericMealPlanExportForPdf(genericPlanId);
  if (response?.success === false) {
    throw new Error(response.message || 'Unable to load cohort meal plan export');
  }

  const normalizedExport = normalizeGenericMealPlanExportForPdf(response?.data ?? response);
  await generateMealPlanPdfFromExportRaw(normalizedExport, {
    ...options,
    planTitle:
      options?.planTitle?.trim()
      || unwrapMealPlanExportData(normalizedExport).name?.trim()
      || 'Cohort Meal Plan',
    singleDayFullWidth: true,
    hideDayStructure: true,
  });
}

/** Fetch a saved meal-plan/meal-slot template and trigger the same PDF layout used by meal plans. */
export async function downloadMealPlanTemplatePdfFromExportApi(
  templateId: string,
  options?: MealPlanPdfDownloadOptions,
): Promise<void> {
  const exportData = await getMealPlanTemplateExportData(templateId);
  await generateMealPlanPdfFromExportRaw(exportData, {
    ...options,
    planTitle: options?.planTitle?.trim() || exportData.name || 'Template',
    singleDayFullWidth: true,
  });
}

/** Fetch GET /meal-plans/{id}/export and trigger a full-width PDF for one recipe item. */
export async function downloadRecipeDetailsPdfFromExportApi(
  mealPlanId: string,
  options: RecipePdfDownloadOptions,
  preloadedRaw?: unknown,
): Promise<void> {
  if (preloadedRaw != null) {
    await generateRecipeDetailsPdfFromExportRaw(preloadedRaw, options);
    return;
  }

  const response = await getMealPlanExportForPdf(mealPlanId);
  if (response?.success === false) {
    throw new Error(response.message || 'Unable to load meal plan export');
  }

  await generateRecipeDetailsPdfFromExportRaw(response?.data ?? response, options);
}

/** Fetch a draft/customized recipe directly and trigger a full-width PDF for that recipe row. */
export async function downloadDraftRecipeDetailsPdfFromRecipeApi(
  draftId: string,
  options: RecipePdfDownloadOptions,
): Promise<void> {
  const recipeId = normalizeExportMatchId(options.recipeId);
  const itemId = normalizeExportMatchId(options.recipeItemId);
  const dayNumber = Number(options.dayNumber ?? 1);
  const lookupMealName = String(options.recipeLookupMealName ?? options.mealName ?? '').trim();
  let lastError: unknown;

  if (recipeId && itemId && Number.isFinite(dayNumber) && lookupMealName) {
    try {
      const customizedResponse = await getCustomizedRecipeDetails(
        recipeId,
        draftId,
        dayNumber,
        lookupMealName,
        itemId,
      );
      if (customizedResponse?.success === false) {
        throw new Error(customizedResponse.message || 'Unable to load customized recipe details.');
      }

      const exportData = buildRecipeDetailsExportDataFromRecipePayload(
        customizedResponse?.data ?? customizedResponse,
        options,
      );
      await generateRecipeDetailsPdfFromExportRaw(exportData, options);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  if (recipeId) {
    try {
      const recipeResponse = await fetchRecipeDetailsByRecipeId(recipeId);
      const exportData = buildRecipeDetailsExportDataFromRecipePayload(recipeResponse, options);
      await generateRecipeDetailsPdfFromExportRaw(exportData, options);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Recipe details are unavailable.');
}

/** Fetch a template and export one recipe row using the meal-plan recipe PDF layout. */
export async function downloadTemplateRecipeDetailsPdfFromExportApi(
  templateId: string,
  options: RecipePdfDownloadOptions,
): Promise<void> {
  const exportData = await getMealPlanTemplateExportData(templateId);
  await generateRecipeDetailsPdfFromExportRaw(exportData, options);
}
